/**
 * In-module mock layer for the Platform Console API.
 *
 * Why not MSW: the repo has NO MSW setup (msw is only a transitive
 * dependency in the lockfile) and adding it would mean a new dependency +
 * the docker named-volume node_modules wipe. This module implements the
 * same contract behind the typed client instead — flip
 * ``CONSOLE_USE_MOCKS`` in ``client.ts`` when the backend lands and this
 * file becomes dead code to delete.
 *
 * The store is STATEFUL within a session so the UI is actually exercisable:
 * created keys appear in the list, revokes stick, "sync now" prepends a
 * run to the history, disconnect flips the connector status.
 */

import {
  actionRiskRequiresConfirm,
  isConsoleActionRisk,
  isValidConnectorActionName,
} from "../lib/connectorActions";
import { isValidCustomConnectorSlug } from "../lib/connectorSlug";
import {
  MANIFEST_DEFAULT_INTERVAL_MINUTES,
  MANIFEST_MIN_INTERVAL_MINUTES,
  isManifestAuthType,
} from "../lib/manifestConfig";
import { validateUploadFile } from "../lib/uploads";
import { CONSOLE_MODEL_PURPOSES, CONSOLE_USAGE_WINDOW_DAYS, CONSOLE_WEBHOOK_EVENTS } from "./types";
import type {
  ConsoleActionDefinition,
  ConsoleActionDefinitionCreate,
  ConsoleActionDefinitionDeleteResponse,
  ConsoleActionDefinitionsResponse,
  ConsoleActionExecuteRequest,
  ConsoleActionInvokeRequest,
  ConsoleActionInvokeResponse,
  ConsoleActionRequest,
  ConsoleActionRequestCreate,
  ConsoleActionRequestsResponse,
  ConsoleAgenticRequest,
  ConsoleAgenticResponse,
  ConsoleAgenticStep,
  ConsoleApiKey,
  ConsoleByokRemovedResponse,
  ConsoleCapability,
  ConsoleConnectResponse,
  ConsoleConnection,
  ConsoleConnectionSchemas,
  ConsoleConnectionSync,
  ConsoleConnector,
  ConsoleConnectorDetail,
  ConsoleConnectorInstance,
  ConsoleConnectorKindsResponse,
  ConsoleConnectorStatus,
  ConsoleConnectorSurface,
  ConsoleEmbeddedIdentity,
  ConsoleDeveloperApps,
  ConsolePersonalConnections,
  ConsoleConnectorsResponse,
  ConsoleConversation,
  ConsoleCreateKeyRequest,
  ConsoleCreateKeyResponse,
  ConsoleCustomConnector,
  ConsoleCustomConnectorCreate,
  ConsoleCustomConnectorDisableResponse,
  ConsoleCustomConnectorUpdate,
  ConsoleCustomConnectorsResponse,
  ConsoleDocument,
  ConsoleDocumentsPage,
  ConsoleFlow,
  ConsoleFlowCreate,
  ConsoleFlowGenerateResult,
  ConsoleFlowPaletteResponse,
  ConsoleFlowRun,
  ConsoleFlowRunApproveResponse,
  ConsoleFlowRunDetail,
  ConsoleFlowRunRequest,
  ConsoleFlowStats,
  ConsoleFlowTemplatesResponse,
  ConsoleFlowUpdate,
  ConsoleFlowsResponse,
  ConsoleJobStatus,
  ConsoleJobsResponse,
  ConsoleKeyAuditEvent,
  ConsoleKeyAuditResponse,
  ConsoleKeysResponse,
  ConsoleModelConfig,
  ConsoleModelConfigResponse,
  ConsoleModelOption,
  ConsoleModelPurpose,
  ConsoleModelRevertResponse,
  ConsoleModelsResponse,
  ConsoleNodePaletteItem,
  ConsoleNodeSchemaResponse,
  ConsoleOverview,
  ConsolePendingApproval,
  ConsolePendingApprovalsResponse,
  ConsoleQueryRequest,
  ConsoleQueryResponse,
  ConsoleRecentJob,
  ConsoleRecordValidateResponse,
  ConsoleRenamedSource,
  ConsoleSchemaObjectCuration,
  ConsoleSchemaObjectView,
  ConsoleSchemaResponse,
  ConsoleSchemaStatus,
  ConsoleScopeInfo,
  ConsoleScopesResponse,
  ConsoleSetModelRequest,
  ConsoleSourceEntry,
  ConsoleSourceState,
  ConsoleSourcesResponse,
  ConsoleSyncListResponse,
  ConsoleUksResponse,
  ConsoleUsage,
  ConsoleUsageBreakdown,
  ConsoleUsageModelEntry,
  ConsoleUsageSourceEntry,
  ConsoleUsageTrendDay,
  ConsoleUsageWindow,
  ConsoleWebhookCreate,
  ConsoleWebhookCreated,
  ConsoleWebhookDeleted,
  ConsoleWebhookDeliveriesResponse,
  ConsoleWebhookDelivery,
  ConsoleWebhookDeliveryReplayed,
  ConsoleWebhookSummary,
  ConsoleWebhookTestResult,
  ConsoleWebhooksResponse,
} from "./types";

/** Simulated network latency so loading states are visible in dev. */
const LATENCY_MS = 220;

function delay(): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, LATENCY_MS));
}

function isoDaysAgo(days: number, hourOffset = 0): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  d.setHours(d.getHours() - hourOffset);
  return d.toISOString();
}

/** Mock error mirroring the ``{"error":{code,message}}`` envelope semantics. */
export class ConsoleMockError extends Error {
  readonly code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = "ConsoleMockError";
    this.code = code;
  }
}

// ── Stateful store ────────────────────────────────────────────────────────

interface MockStore {
  personalConnections: ConsolePersonalConnections;
  embeddedIdentity: ConsoleEmbeddedIdentity;
  developerApps: ConsoleDeveloperApps;
  keys: ConsoleApiKey[];
  auditByKey: Map<string, ConsoleKeyAuditEvent[]>;
  /** Raw connections (one row per account/database) — the catalog + deep-dive
   * are both DERIVED from these, exactly like the real bridge. */
  connections: MockConnection[];
  /** The project's own Push-API connectors (newest first). */
  customConnectors: ConsoleCustomConnector[];
  /** Declared actions (the write-loop catalog), keyed by connector slug. */
  actionDefs: Map<string, ConsoleActionDefinition[]>;
  /** Every proposed/executed action request (newest first). */
  actionRequests: ConsoleActionRequest[];
  /** Connector slugs with a webhook subscribed to ``action.requested``. */
  actionWebhooks: Set<string>;
  actionSeq: number;
  /** Registered webhook endpoints (newest first) — never carry the secret. */
  webhooks: ConsoleWebhookSummary[];
  webhookSeq: number;
  keySeq: number;
  /** Session-uploaded documents (newest first) — listed before the fixtures. */
  uploadedDocs: ConsoleDocument[];
  /** Ids removed via delete — fixture AND uploaded docs are filtered by this. */
  deletedDocIds: Set<string>;
  docSeq: number;
  /**
   * Project-level model overrides per purpose (the ``tenant`` rung). Absent =
   * the purpose inherits the platform default. ``byokSecret`` holds the
   * PLAINTEXT customer key so the mock can mask it on read — the wire NEVER
   * carries it (same guarantee as the backend).
   */
  modelConfigs: Map<ConsoleModelPurpose, MockModelOverride>;
  /** Publish gate per structured source: sourceId → published_at ISO (present = published). */
  schemaPublished: Map<string, string>;
  /** Admin display-name alias per structured source: sourceId → name. */
  sourceAlias: Map<string, string>;
  /** Human curation overrides: sourceId → objectName → the override. */
  schemaCuration: Map<string, Map<string, ConsoleSchemaObjectCuration>>;
  /** The project's flows (console projection of Hive Flows), newest first. */
  flows: ConsoleFlow[];
  flowRunSeq: number;
  /** Ids for flows created in-session (template clone / AI draft). */
  flowSeq: number;
}

interface MockModelOverride {
  provider: string;
  model: string;
  params: Record<string, unknown>;
  byokSecret: string | null;
  updatedAt: string;
  runtimeEnabled?: boolean;
}

// ── Connections model (mirrors the real bridge) ────────────────────────────
//
// One row per account/database. Both the catalog and the deep-dive are
// derived from these — exactly like ``connectors.py``. ``provider`` is an
// internal vendor slug (NEVER shown); the FE labels everything by capability.

interface MockConnection {
  connection_id: string;
  /** White-label capability id (crm/drive/database/…). */
  capability: string;
  connection_type: string;
  surface: "saas" | "structured";
  /** Internal vendor slug — routing detail only. */
  provider: string;
  /** Display-safe account/db name. */
  label: string;
  scope: string | null;
  expires_at: string | null;
  created_at: string;
  last_used_at: string | null;
  /** Drives token_status + the broken state. */
  needsReauth: boolean;
  /** Structured mid-ingestion marker → status "syncing". */
  ingesting: boolean;
  /** SaaS sync_state (email/chat/drive). */
  sync: ConsoleConnectionSync | null;
  /** Structured discovered schema. */
  schemas: ConsoleConnectionSchemas | null;
}

/** White-label capability catalog (id → connection_type + surface + vendors). */
const CAPABILITY_CATALOG: ReadonlyArray<{
  readonly type: string;
  readonly connection_type: string;
  readonly surface: ConsoleConnectorSurface;
  readonly vendors: readonly string[];
}> = [
  { type: "crm", connection_type: "crm", surface: "saas", vendors: ["salesforce", "hubspot"] },
  {
    type: "project_management",
    connection_type: "project_management",
    surface: "saas",
    vendors: ["trello"],
  },
  {
    type: "accounting",
    connection_type: "accounting",
    surface: "saas",
    vendors: ["fatture_in_cloud", "sap_b1"],
  },
  {
    type: "calendar",
    connection_type: "calendar",
    surface: "saas",
    vendors: ["google_calendar", "outlook"],
  },
  {
    type: "messaging",
    connection_type: "chat",
    surface: "saas",
    vendors: ["teams", "google_chat", "slack", "whatsapp"],
  },
  {
    type: "drive",
    connection_type: "drive",
    surface: "saas",
    vendors: ["google_drive", "onedrive", "sharepoint", "notion"],
  },
  { type: "email", connection_type: "email", surface: "saas", vendors: ["gmail", "outlook"] },
  {
    type: "pec",
    connection_type: "email",
    surface: "saas",
    vendors: ["aruba_pec", "infocert_pec"],
  },
  {
    type: "open_banking",
    connection_type: "banking",
    surface: "mixed",
    vendors: ["acube", "banking"],
  },
  {
    type: "cassetto_fiscale",
    connection_type: "cassetto_fiscale",
    surface: "saas",
    vendors: ["acube"],
  },
  {
    type: "cad_library",
    connection_type: "filesystem",
    surface: "mixed",
    vendors: ["autocad"],
  },
  {
    type: "database",
    connection_type: "enterprise",
    surface: "structured",
    vendors: ["postgresql", "mysql", "sqlserver", "oracle", "sqlite", "generic_odbc"],
  },
  {
    type: "erp",
    connection_type: "enterprise",
    surface: "structured",
    vendors: ["sap_odata", "sap_b1", "zucchetti", "teamsystem"],
  },
  {
    type: "spreadsheet",
    connection_type: "enterprise",
    surface: "structured",
    vendors: ["excel_online", "google_sheets", "csv"],
  },
];

function catalogEntry(capability: string): (typeof CAPABILITY_CATALOG)[number] | undefined {
  return CAPABILITY_CATALOG.find((c) => c.type === capability);
}

function connectionStatus(conn: MockConnection): "connected" | "error" | "syncing" {
  if (conn.surface === "structured" && conn.ingesting) return "syncing";
  return conn.needsReauth ? "error" : "connected";
}

function connectionLastSyncAt(conn: MockConnection): string | null {
  if (conn.sync) return conn.sync.last_sync_at;
  return conn.schemas?.last_discovered_at ?? null;
}

/** The reconnect entrypoint the product would return for a connection. */
function reconnectEntry(capability: string, vendor: string, surface: "saas" | "structured") {
  return surface === "structured"
    ? { redirect_url: "/api/v1/enterprise/connectors", flow: "credentials_form", vendor }
    : {
        redirect_url: `/api/v1/connectors/${capability}/start`,
        flow: "oauth_redirect",
        vendor,
      };
}

/** Derive the wire ``ConsoleConnection`` view of one raw connection. */
function connectionView(conn: MockConnection): ConsoleConnection {
  const status = connectionStatus(conn);
  const objectCount = conn.schemas?.object_count ?? null;
  const recordCount = conn.schemas?.record_count ?? null;
  const items = conn.sync?.items_synced ?? null;
  const chunks = conn.sync?.total_chunks ?? null;
  const usage =
    conn.surface === "structured"
      ? recordCount
        ? { figure: recordCount, kind: "records" }
        : null
      : items
        ? { figure: items, kind: "items" }
        : null;
  return {
    connection_id: conn.connection_id,
    provider: conn.provider,
    label: conn.label,
    connection_identifier: conn.label,
    token_status: conn.needsReauth ? "needs_reauth" : "active",
    status,
    needs_reauth: conn.needsReauth,
    scope: conn.scope,
    expires_at: conn.expires_at,
    created_at: conn.created_at,
    last_used_at: conn.last_used_at,
    last_sync_at: connectionLastSyncAt(conn),
    sync: conn.sync,
    schemas: conn.schemas,
    counts: {
      rows: recordCount,
      chunks,
      objects: objectCount,
      items,
    },
    usage,
    reconnect: reconnectEntry(conn.capability, conn.provider, conn.surface),
    disconnect: { method: "DELETE", path: `/connectors/${conn.connection_id}` },
  };
}

function buildInitialStore(): MockStore {
  const keys: ConsoleApiKey[] = [
    {
      key_id: "key_01J9ZC4T8",
      name: "Produzione — sito docs",
      key_prefix: "ando_live_7f3a",
      scopes: ["query:read", "generate:write"],
      resource_policy: { sources: ["drive", "upload"], monthly_spend_limit_cents: 5000 },
      created_at: isoDaysAgo(41),
      expires_at: null,
      last_used_at: isoDaysAgo(0, 3),
      revoked_at: null,
    },
    {
      key_id: "key_01J9ZC4T9",
      name: "Staging — QA bot",
      key_prefix: "ando_test_c21d",
      scopes: ["query:read"],
      resource_policy: null,
      created_at: isoDaysAgo(12),
      expires_at: isoDaysAgo(-60),
      last_used_at: isoDaysAgo(2),
      revoked_at: null,
    },
    {
      key_id: "key_01J9ZC4TA",
      name: "Vecchia integrazione CRM",
      key_prefix: "ando_live_9b02",
      scopes: ["query:read", "generate:write", "ingest:write"],
      resource_policy: { connector_ids: ["conn_hubspot_1"] },
      created_at: isoDaysAgo(190),
      expires_at: null,
      last_used_at: isoDaysAgo(95),
      revoked_at: isoDaysAgo(90),
    },
  ];

  // Event vocabulary per contract: created / used / verify_failed /
  // scope_denied / revoked. Newest first.
  const auditByKey = new Map<string, ConsoleKeyAuditEvent[]>([
    [
      "key_01J9ZC4T8",
      [
        { event: "used", detail: "POST /query (retrieve, k=8)", created_at: isoDaysAgo(0, 3) },
        {
          event: "scope_denied",
          detail: "POST /query rifiutata: scope 'ingest:write' non incluso nella chiave",
          created_at: isoDaysAgo(0, 9),
        },
        { event: "used", detail: "POST /query (answer, k=5)", created_at: isoDaysAgo(1) },
        {
          event: "created",
          detail: "Creata da andrea@example.com",
          created_at: isoDaysAgo(41),
        },
      ],
    ],
    [
      "key_01J9ZC4T9",
      [
        {
          event: "verify_failed",
          detail: "Firma non valida: chiave troncata o corrotta",
          created_at: isoDaysAgo(1),
        },
        { event: "used", detail: "POST /query (retrieve, k=4)", created_at: isoDaysAgo(2) },
        {
          event: "created",
          detail: "Creata da andrea@example.com",
          created_at: isoDaysAgo(12),
        },
      ],
    ],
    [
      "key_01J9ZC4TA",
      [
        {
          event: "revoked",
          detail: "Revocata da andrea@example.com",
          created_at: isoDaysAgo(90),
        },
        {
          event: "verify_failed",
          detail: "Tentativo d'uso dopo la revoca",
          created_at: isoDaysAgo(88),
        },
        { event: "created", detail: "Creata da ops@example.com", created_at: isoDaysAgo(190) },
      ],
    ],
  ]);

  // Seed connections — a story that exercises every deep-dive surface:
  // a capability with TWO accounts (one broken → reconnect), a healthy
  // SaaS sync, an errored capability, and structured DB connections with
  // discovered schemas + one mid-ingestion (syncing).
  const connections: MockConnection[] = [
    // ── email: two mailboxes, second needs re-auth (N never collapses) ──
    {
      connection_id: "conn_email_1",
      capability: "email",
      connection_type: "email",
      surface: "saas",
      provider: "gmail",
      label: "andrea@example.com",
      scope: "https://www.googleapis.com/auth/gmail.readonly",
      expires_at: isoDaysAgo(-25),
      created_at: isoDaysAgo(64),
      last_used_at: isoDaysAgo(0, 2),
      needsReauth: false,
      ingesting: false,
      sync: {
        sync_status: "completed",
        sync_error: null,
        last_full_sync_at: isoDaysAgo(6),
        last_incremental_sync_at: isoDaysAgo(0, 2),
        last_sync_at: isoDaysAgo(0, 2),
        items_synced: 1284,
        total_chunks: 5321,
      },
      schemas: null,
    },
    {
      connection_id: "conn_email_2",
      capability: "email",
      connection_type: "email",
      surface: "saas",
      provider: "gmail",
      label: "ops@example.com",
      scope: "https://www.googleapis.com/auth/gmail.readonly",
      expires_at: isoDaysAgo(2),
      created_at: isoDaysAgo(40),
      last_used_at: isoDaysAgo(3),
      needsReauth: true,
      ingesting: false,
      sync: {
        sync_status: "error",
        sync_error: "Token OAuth scaduto: riautorizzare la connessione.",
        last_full_sync_at: isoDaysAgo(9),
        last_incremental_sync_at: isoDaysAgo(3),
        last_sync_at: isoDaysAgo(3),
        items_synced: 402,
        total_chunks: 1740,
      },
      schemas: null,
    },
    // ── drive: healthy ──
    {
      connection_id: "conn_drive_1",
      capability: "drive",
      connection_type: "drive",
      surface: "saas",
      provider: "google_drive",
      label: "Drive — Andrea Bovinelli",
      scope: "https://www.googleapis.com/auth/drive.readonly",
      expires_at: isoDaysAgo(-20),
      created_at: isoDaysAgo(72),
      last_used_at: isoDaysAgo(0, 1),
      needsReauth: false,
      ingesting: false,
      sync: {
        sync_status: "completed",
        sync_error: null,
        last_full_sync_at: isoDaysAgo(4),
        last_incremental_sync_at: isoDaysAgo(0, 1),
        last_sync_at: isoDaysAgo(0, 1),
        items_synced: 842,
        total_chunks: 3980,
      },
      schemas: null,
    },
    // ── crm: healthy ──
    {
      connection_id: "conn_crm_1",
      capability: "crm",
      connection_type: "crm",
      surface: "saas",
      provider: "hubspot",
      label: "ACME Srl",
      scope: "crm.objects.contacts.read crm.objects.deals.read",
      expires_at: isoDaysAgo(-11),
      created_at: isoDaysAgo(55),
      last_used_at: isoDaysAgo(0, 7),
      needsReauth: false,
      ingesting: false,
      sync: {
        sync_status: "completed",
        sync_error: null,
        last_full_sync_at: isoDaysAgo(2),
        last_incremental_sync_at: isoDaysAgo(0, 7),
        last_sync_at: isoDaysAgo(0, 7),
        items_synced: 342,
        total_chunks: 1206,
      },
      schemas: null,
    },
    // ── project_management: single account, broken (capability errore) ──
    {
      connection_id: "conn_pm_1",
      capability: "project_management",
      connection_type: "project_management",
      surface: "saas",
      provider: "trello",
      label: "Workspace Prodotto",
      scope: "read",
      expires_at: isoDaysAgo(1),
      created_at: isoDaysAgo(30),
      last_used_at: isoDaysAgo(3),
      needsReauth: true,
      ingesting: false,
      sync: {
        sync_status: "error",
        sync_error: "Token OAuth scaduto: riautorizzare la connessione.",
        last_full_sync_at: isoDaysAgo(9),
        last_incremental_sync_at: isoDaysAgo(3),
        last_sync_at: isoDaysAgo(3),
        items_synced: 87,
        total_chunks: 310,
      },
      schemas: null,
    },
    // ── database: two DBs, one healthy w/ schemas, one mid-ingestion ──
    {
      connection_id: "conn_db_1",
      capability: "database",
      connection_type: "enterprise",
      surface: "structured",
      provider: "postgresql",
      label: "Gestionale — Produzione",
      scope: null,
      expires_at: null,
      created_at: isoDaysAgo(48),
      last_used_at: isoDaysAgo(0, 5),
      needsReauth: false,
      ingesting: false,
      sync: null,
      schemas: {
        object_count: 12,
        record_count: 48210,
        last_discovered_at: isoDaysAgo(0, 5),
        objects: [
          { name: "clienti", record_count: 3120 },
          { name: "fornitori", record_count: 842 },
          { name: "ordini", record_count: 15430 },
          { name: "righe_ordine", record_count: 24110 },
          { name: "prodotti", record_count: 4508 },
          { name: "listini", record_count: 200 },
        ],
      },
    },
    {
      connection_id: "conn_db_2",
      capability: "database",
      connection_type: "enterprise",
      surface: "structured",
      provider: "mysql",
      label: "Analytics — Replica",
      scope: null,
      expires_at: null,
      created_at: isoDaysAgo(6),
      last_used_at: isoDaysAgo(0, 1),
      needsReauth: false,
      ingesting: true,
      sync: null,
      schemas: {
        object_count: 0,
        record_count: null,
        last_discovered_at: null,
        objects: [],
      },
    },
    // ── spreadsheet: structured with a small discovered schema ──
    {
      connection_id: "conn_sheet_1",
      capability: "spreadsheet",
      connection_type: "enterprise",
      surface: "structured",
      provider: "google_sheets",
      label: "Preventivi 2026",
      scope: null,
      expires_at: null,
      created_at: isoDaysAgo(14),
      last_used_at: isoDaysAgo(1),
      needsReauth: false,
      ingesting: false,
      sync: null,
      schemas: {
        object_count: 3,
        record_count: 1520,
        last_discovered_at: isoDaysAgo(1),
        objects: [
          { name: "Preventivi", record_count: 940 },
          { name: "Anagrafica", record_count: 560 },
          { name: "Sconti", record_count: 20 },
        ],
      },
    },
  ];

  // Two developer-registered Push-API connectors so the "I miei connettori"
  // list is exercisable out of the box: one busy (records + recent sync), one
  // freshly registered (never synced).
  const customConnectors: ConsoleCustomConnector[] = [
    {
      slug: "warehouse-events",
      name: "Eventi magazzino",
      kind: "push",
      active: true,
      config: {},
      created_at: isoDaysAgo(2),
      updated_at: isoDaysAgo(2),
      last_sync_at: null,
      record_count: 0,
    },
    {
      slug: "acme-crm",
      name: "Acme CRM",
      kind: "push",
      active: true,
      config: { environment: "production", region: "eu" },
      created_at: isoDaysAgo(21),
      updated_at: isoDaysAgo(0, 4),
      last_sync_at: isoDaysAgo(0, 4),
      record_count: 3421,
    },
  ];

  // Declared actions (the write-loop) so the surface is exercisable out of the
  // box: acme-crm carries a low- and a high-risk action AND has a webhook
  // subscribed (execute → dispatched); warehouse-events carries one action but
  // NO webhook (execute → failed + the "register a webhook" 400).
  const actionDefs = new Map<string, ConsoleActionDefinition[]>([
    [
      "acme-crm",
      [
        {
          connector: "acme-crm",
          name: "create.ticket",
          description: "Apre un ticket di supporto nel tuo CRM.",
          params_schema: {
            type: "object",
            properties: {
              title: { type: "string", example: "Cliente bloccato al checkout" },
              priority: { type: "string" },
            },
            required: ["title"],
            additionalProperties: false,
          },
          risk: "low",
          created_at: isoDaysAgo(20),
          updated_at: isoDaysAgo(20),
        },
        {
          connector: "acme-crm",
          name: "delete.contact",
          description: "Elimina definitivamente un contatto. Irreversibile.",
          params_schema: {
            type: "object",
            properties: { contact_id: { type: "string" } },
            required: ["contact_id"],
            additionalProperties: false,
          },
          risk: "high",
          created_at: isoDaysAgo(9),
          updated_at: isoDaysAgo(9),
        },
      ],
    ],
    [
      "warehouse-events",
      [
        {
          connector: "warehouse-events",
          name: "record.movement",
          description: "Registra un movimento di magazzino.",
          params_schema: {
            type: "object",
            properties: {
              sku: { type: "string" },
              qty: { type: "integer" },
            },
            required: ["sku", "qty"],
            additionalProperties: false,
          },
          risk: "medium",
          created_at: isoDaysAgo(1),
          updated_at: isoDaysAgo(1),
        },
      ],
    ],
  ]);

  return {
    personalConnections: { connections: [] },
    embeddedIdentity: { issuers: [], subjects: [], return_urls: [] },
    developerApps: { apps: [] },
    keys,
    auditByKey,
    connections,
    customConnectors,
    actionDefs,
    actionRequests: [],
    actionWebhooks: new Set<string>(["acme-crm"]),
    actionSeq: 0,
    // One seeded endpoint (reads only — NOT action.requested) so the listing
    // is exercisable and the write-loop still teaches "register a webhook".
    webhooks: [
      {
        webhook_id: "wh_seed_1",
        url: "https://example.com/hooks/ando",
        events: ["ingestion.completed", "source.sync.completed"],
        active: true,
        created_at: isoDaysAgo(10),
      },
    ],
    webhookSeq: 1,
    keySeq: 100,
    uploadedDocs: [],
    deletedDocIds: new Set<string>(),
    docSeq: 0,
    modelConfigs: new Map<ConsoleModelPurpose, MockModelOverride>(),
    schemaPublished: new Map<string, string>(),
    sourceAlias: new Map<string, string>(),
    schemaCuration: new Map<string, Map<string, ConsoleSchemaObjectCuration>>(),
    flows: buildInitialFlows(),
    flowRunSeq: 0,
    flowSeq: 0,
  };
}

let store: MockStore = buildInitialStore();

export function consoleIdentityMockStore(): Pick<
  MockStore,
  "personalConnections" | "embeddedIdentity" | "developerApps"
> {
  return store;
}

/** Test hook — restore pristine fixtures. */
export function resetConsoleMockStore(): void {
  store = buildInitialStore();
}

// ── Documents fixture (deterministic, 57 rows) ────────────────────────────

const DOC_NAMES = [
  "Listino_2026.pdf",
  "Contratto_quadro_ACME.docx",
  "Specifica_tecnica_v4.pdf",
  "Onboarding_fornitori.pptx",
  "Verbale_CDA_giugno.pdf",
  "Preventivo_1043.xlsx",
  "Manuale_qualita.pdf",
  "Offerta_commerciale_beta.docx",
] as const;

const DOC_SOURCES = ["drive", "upload", "hubspot", "trello"] as const;

const DOC_STATUSES: readonly ConsoleJobStatus[] = [
  "completed",
  "completed",
  "completed",
  "completed",
  "running",
  "failed",
  "queued",
];

const TOTAL_DOCUMENTS = 57;
const PAGE_SIZE = 20;

function buildDocument(index: number): ConsoleDocument {
  const name = DOC_NAMES[index % DOC_NAMES.length] ?? "Documento.pdf";
  const source = DOC_SOURCES[index % DOC_SOURCES.length] ?? "upload";
  const status = DOC_STATUSES[index % DOC_STATUSES.length] ?? "completed";
  return {
    id: `doc_${1000 + index}`,
    name: `${String(index + 1).padStart(2, "0")}_${name}`,
    source,
    chunks: status === "completed" ? 12 + ((index * 7) % 140) : 0,
    size_bytes: 40_000 + ((index * 913_777) % 8_000_000),
    status,
    created_at: isoDaysAgo(index % 45, index % 12),
  };
}

// ── Usage fixture (deterministic wave, window-sized) ──────────────────────

function buildUsage(dayCount: number): ConsoleUsage {
  const days = Array.from({ length: dayCount }, (_, i) => {
    const idx = dayCount - 1 - i;
    const wave = Math.sin(i / 4.2) * 0.5 + 0.5;
    const weekday = new Date(isoDaysAgo(idx)).getDay();
    const weekend = weekday === 0 || weekday === 6 ? 0.25 : 1;
    const queries = Math.round((40 + wave * 180) * weekend);
    return {
      date: isoDaysAgo(idx).slice(0, 10),
      queries,
      // ~$0.0021/query on the same wave — a plausible marked-up spend curve.
      cost_usd: Number((queries * 0.0021 * (0.9 + wave * 0.3)).toFixed(4)),
      pages_ingested: Math.round((10 + wave * 90) * weekend),
      storage_bytes: 4_100_000_000 + i * 38_000_000,
    };
  });
  const totalPages = days.reduce((a, d) => a + d.pages_ingested, 0);
  return {
    days,
    totals: {
      queries: days.reduce((a, d) => a + d.queries, 0),
      cost_usd: Number(days.reduce((a, d) => a + d.cost_usd, 0).toFixed(4)),
      documents: Math.round(totalPages / 6),
      pages_ingested: totalPages,
      storage_bytes: days.reduce((a, d) => a + d.storage_bytes, 0),
      storage_bytes_current: days[days.length - 1]?.storage_bytes ?? 0,
    },
  };
}

// ── Usage breakdown fixture (requests + tokens, by model / purpose) ────────

/** Per-day requests + a plausible ~1.3k tokens/request wave. */
function buildTrendDays(dayCount: number): ConsoleUsageTrendDay[] {
  return Array.from({ length: dayCount }, (_, i) => {
    const idx = dayCount - 1 - i;
    const wave = Math.sin(i / 4.2) * 0.5 + 0.5;
    const weekday = new Date(isoDaysAgo(idx)).getDay();
    const weekend = weekday === 0 || weekday === 6 ? 0.25 : 1;
    const requests = Math.round((40 + wave * 180) * weekend);
    return {
      date: isoDaysAgo(idx).slice(0, 10),
      requests,
      tokens: Math.round(requests * 1350 * (0.9 + wave * 0.3)),
    };
  });
}

/** Split a window total across fixed fractions, remainder to the first slice. */
function splitByFraction<T>(
  total: number,
  parts: ReadonlyArray<{ readonly fraction: number; readonly rest: T }>,
): Array<T & { readonly value: number }> {
  let assigned = 0;
  return parts.map((part, i) => {
    const value =
      i === parts.length - 1 ? Math.max(0, total - assigned) : Math.round(total * part.fraction);
    assigned += value;
    return { ...part.rest, value };
  });
}

const MOCK_MODEL_MIX = [
  { fraction: 0.58, model: "gemini-3-flash", provider: "google" },
  { fraction: 0.24, model: "gemini-3-pro", provider: "google" },
  { fraction: 0.12, model: "text-embedding-005", provider: "google" },
  { fraction: 0.06, model: "claude-opus-4-8", provider: "anthropic" },
] as const;

const MOCK_SOURCE_MIX = [
  { fraction: 0.46, source: "agent_query" },
  { fraction: 0.27, source: "entity_extraction" },
  { fraction: 0.17, source: "email_sync" },
  { fraction: 0.1, source: "canvas_compile" },
] as const;

function buildUsageBreakdown(window: ConsoleUsageWindow): ConsoleUsageBreakdown {
  const dayCount = CONSOLE_USAGE_WINDOW_DAYS[window];
  const days = buildTrendDays(dayCount);
  const requests = days.reduce((acc, d) => acc + d.requests, 0);
  const tokens = days.reduce((acc, d) => acc + d.tokens, 0);

  const by_model: ConsoleUsageModelEntry[] = splitByFraction(
    tokens,
    MOCK_MODEL_MIX.map((m) => ({ fraction: m.fraction, rest: m })),
  ).map((row) => ({
    model: row.model,
    provider: row.provider,
    tokens: row.value,
    requests: Math.round(requests * row.fraction),
  }));

  const by_source: ConsoleUsageSourceEntry[] = splitByFraction(
    tokens,
    MOCK_SOURCE_MIX.map((s) => ({ fraction: s.fraction, rest: s })),
  ).map((row) => ({
    source: row.source,
    tokens: row.value,
    requests: Math.round(requests * row.fraction),
  }));

  return {
    window,
    days,
    totals: { requests, tokens },
    // Previous period ran ~14% lighter — a visible positive delta.
    previous: {
      requests: Math.round(requests * 0.86),
      tokens: Math.round(tokens * 0.86),
    },
    by_model,
    by_source,
  };
}

// ── Connectors: derive catalog + deep-dive from raw connections ────────────

const USABLE_CONNECTION_STATUSES = new Set(["connected", "syncing"]);

/** Sum the headline figure across a capability's connections. */
function sumUsageFigure(conns: MockConnection[]): number {
  return conns.reduce((acc, c) => acc + (c.sync?.items_synced ?? c.schemas?.record_count ?? 0), 0);
}

/** Build one catalog entry (ConsoleConnector) for a capability. */
function buildCatalogEntry(
  type: string,
  connectionType: string,
  catalogSurface: ConsoleConnectorSurface,
  vendors: readonly string[],
  conns: MockConnection[],
): ConsoleConnector {
  const providers: Record<string, { active: number; total: number }> = {};
  const instances: ConsoleConnectorInstance[] = [];
  const surfaces = new Set<string>();
  let active = 0;
  let lastSync: string | null = null;

  for (const conn of conns) {
    const status = connectionStatus(conn);
    surfaces.add(conn.surface);
    let stat = providers[conn.provider];
    if (!stat) {
      stat = { active: 0, total: 0 };
      providers[conn.provider] = stat;
    }
    stat.total += 1;
    if (USABLE_CONNECTION_STATUSES.has(status)) {
      stat.active += 1;
      active += 1;
    }
    instances.push({
      connection_id: conn.connection_id,
      name: conn.label,
      provider: conn.provider,
      status,
      schemas: conn.schemas?.object_count ?? 0,
      last_sync_at: connectionLastSyncAt(conn),
      created_at: conn.created_at,
      last_used_at: conn.last_used_at,
    });
    const ls = connectionLastSyncAt(conn);
    if (ls && (!lastSync || ls > lastSync)) lastSync = ls;
  }

  const total = conns.length;
  const surface: ConsoleConnectorSurface =
    surfaces.size === 0
      ? catalogSurface
      : surfaces.has("saas") && surfaces.has("structured")
        ? "mixed"
        : surfaces.has("structured")
          ? "structured"
          : "saas";
  const status: ConsoleConnectorStatus =
    active > 0 ? "connected" : total > 0 ? "error" : "not_connected";
  const first = instances[0];

  return {
    type,
    connection_type: connectionType,
    surface,
    vendors: [...vendors],
    status,
    connections: total,
    active_connections: active,
    providers,
    last_sync_at: lastSync,
    instances,
    // Legacy compat for the Keys resource-scoping picker (see types.ts): the
    // real enriched catalog no longer carries these top-level fields.
    ...(first
      ? {
          connection_id: first.connection_id,
          label: first.name,
          items_count: sumUsageFigure(conns),
        }
      : {}),
  };
}

/** The full catalog (ConsoleConnector[]) derived from the raw connections. */
function deriveConnectors(): ConsoleConnector[] {
  const byCapability = new Map<string, MockConnection[]>();
  for (const conn of store.connections) {
    const list = byCapability.get(conn.capability) ?? [];
    list.push(conn);
    byCapability.set(conn.capability, list);
  }

  const entries: ConsoleConnector[] = [];
  const seen = new Set<string>();
  for (const cap of CAPABILITY_CATALOG) {
    seen.add(cap.type);
    entries.push(
      buildCatalogEntry(
        cap.type,
        cap.connection_type,
        cap.surface,
        cap.vendors,
        byCapability.get(cap.type) ?? [],
      ),
    );
  }
  // Defensive: connections for a capability absent from the catalog.
  for (const [capability, conns] of byCapability) {
    if (seen.has(capability)) continue;
    const first = conns[0];
    entries.push(
      buildCatalogEntry(
        capability,
        first?.connection_type ?? "enterprise",
        first?.surface ?? "saas",
        [],
        conns,
      ),
    );
  }
  return entries.sort((a, b) => a.type.localeCompare(b.type));
}

// ── Endpoint implementations ──────────────────────────────────────────────

export async function mockOverview(): Promise<ConsoleOverview> {
  await delay();
  const recentJobs: ConsoleRecentJob[] = Array.from({ length: 8 }, (_, i) => {
    const doc = buildDocument(i * 3);
    return {
      id: `job_${770 - i}`,
      status: doc.status,
      document: doc.name,
      created_at: isoDaysAgo(0, i * 5 + 1),
    };
  });
  const connectors = deriveConnectors();
  return {
    documents: TOTAL_DOCUMENTS,
    chunks: 6431,
    storage_bytes: 5_240_000_000,
    connectors_active: connectors.filter((c) => c.status === "connected").length,
    recent_jobs: recentJobs,
    last_sync_by_connector: connectors
      .filter((c) => c.last_sync_at !== null)
      .map((c) => ({
        type: c.type,
        at: c.last_sync_at ?? "",
        status: c.status === "error" ? ("error" as const) : ("ok" as const),
      })),
  };
}

export async function mockListKeys(): Promise<ConsoleKeysResponse> {
  await delay();
  return { keys: store.keys.map((k) => ({ ...k })) };
}

/**
 * ``GET /keys/scopes`` server data — the 8 REAL wire ids, mirrored from the
 * BE catalog (``ando-agent/src/api/platform/console/keys.py``). Fixture
 * INSIDE the mock layer on purpose: the live path has no hardcoded copy —
 * ``/keys/scopes`` plus its last-known-good cache are the only seams (see
 * ``fetchConsoleKeyScopes`` in ``client.ts``).
 */
const MOCK_SCOPE_CATALOG: readonly ConsoleScopeInfo[] = [
  {
    id: "ingest:write",
    label: "Ingestion",
    description: "Carica e indicizza nuovi contenuti via API.",
  },
  {
    id: "query:read",
    label: "Retrieval",
    description: "Cerca nei contenuti indicizzati e recupera i passaggi rilevanti.",
  },
  {
    id: "generate:write",
    label: "Generazione",
    description: "Genera risposte con citazioni basate sui documenti.",
  },
  {
    id: "sources:manage",
    label: "Sorgenti",
    description: "Gestisce connettori e sorgenti di contenuto.",
  },
  {
    id: "usage:read",
    label: "Usage",
    description: "Legge metriche di consumo e statistiche di utilizzo.",
  },
  {
    id: "keys:manage",
    label: "Chiavi",
    description: "Amministra le AppKeys dell'organizzazione via API.",
  },
  {
    id: "cad:read",
    label: "CAD — lettura",
    description: "Consulta la libreria CAD e i risultati di analisi.",
  },
  {
    id: "cad:write",
    label: "CAD — scrittura",
    description: "Carica modelli CAD e avvia nuove analisi.",
  },
];

export async function mockListKeyScopes(): Promise<ConsoleScopesResponse> {
  await delay();
  return { scopes: MOCK_SCOPE_CATALOG.map((s) => ({ ...s })) };
}

export async function mockCreateKey(
  input: ConsoleCreateKeyRequest,
): Promise<ConsoleCreateKeyResponse> {
  await delay();
  const name = input.name.trim();
  if (name.length === 0) {
    throw new ConsoleMockError("invalid_request", "Il nome della chiave è obbligatorio.");
  }
  if (input.scopes.length === 0) {
    throw new ConsoleMockError("invalid_request", "Seleziona almeno uno scope.");
  }
  // Mirror the live API: unknown scope ids are a 400, not silently accepted.
  const knownScopeIds = new Set(MOCK_SCOPE_CATALOG.map((s) => s.id));
  const unknown = input.scopes.filter((s) => !knownScopeIds.has(s));
  if (unknown.length > 0) {
    throw new ConsoleMockError("invalid_request", `Unknown scope(s): ${unknown.join(", ")}`);
  }
  store.keySeq += 1;
  const suffix = store.keySeq.toString(36).padStart(4, "0");
  const live = input.live ?? true;
  const principalMode = input.principal_mode ?? "service";
  if (principalMode === "end_user_only" && (!live || !input.end_user_issuer)) {
    throw new ConsoleMockError(
      "invalid_request",
      "Una chiave per utenti finali richiede un issuer e l’ambiente live.",
    );
  }
  const prefix = `ando_${live ? "live" : "test"}_${suffix}`;
  const keyId = `key_mock_${store.keySeq}`;
  const record: ConsoleApiKey = {
    key_id: keyId,
    name,
    key_prefix: prefix,
    scopes: [...input.scopes],
    resource_policy: input.resource_policy ?? null,
    created_at: new Date().toISOString(),
    expires_at: input.expires_at ?? null,
    last_used_at: null,
    revoked_at: null,
    principal_mode: principalMode,
    end_user_issuer: principalMode === "end_user_only" ? input.end_user_issuer : null,
  };
  store.keys = [record, ...store.keys];
  store.auditByKey.set(keyId, [
    {
      event: "created",
      detail: `Creata (${input.scopes.join(", ")})`,
      created_at: record.created_at,
    },
  ]);
  return {
    api_key: `${prefix}_${crypto.randomUUID().replaceAll("-", "")}`,
    key_id: keyId,
    key_prefix: prefix,
    scopes: [...input.scopes],
    principal_mode: principalMode,
    end_user_issuer: record.end_user_issuer,
  };
}

export async function mockRevokeKey(keyId: string): Promise<void> {
  await delay();
  const key = store.keys.find((k) => k.key_id === keyId);
  if (!key) {
    throw new ConsoleMockError("not_found", "Chiave non trovata.");
  }
  if (key.revoked_at !== null) {
    throw new ConsoleMockError("conflict", "La chiave è già stata revocata.");
  }
  key.revoked_at = new Date().toISOString();
  const audit = store.auditByKey.get(keyId) ?? [];
  audit.unshift({
    event: "revoked",
    detail: "Revocata dalla console",
    created_at: key.revoked_at,
  });
  store.auditByKey.set(keyId, audit);
}

export async function mockKeyAudit(keyId: string): Promise<ConsoleKeyAuditResponse> {
  await delay();
  return { events: [...(store.auditByKey.get(keyId) ?? [])] };
}

export async function mockListConnectors(): Promise<ConsoleConnectorsResponse> {
  await delay();
  const connectors = deriveConnectors();
  return { connectors, count: connectors.length, degraded: false, personal_connections: [] };
}

export async function mockConnectorDetail(connectionId: string): Promise<ConsoleConnectorDetail> {
  await delay();
  const focus = store.connections.find((c) => c.connection_id === connectionId);
  if (!focus) {
    throw new ConsoleMockError("not_found", "Connessione non trovata.");
  }
  // Return EVERY connection of the focused capability — the deep-dive never
  // collapses a capability with N accounts to one.
  const peers = store.connections.filter((c) => c.capability === focus.capability);
  const connections = peers.map(connectionView);

  const cat = catalogEntry(focus.capability);
  const surfaces = new Set(peers.map((c) => c.surface));
  const surface: ConsoleConnectorSurface =
    surfaces.has("saas") && surfaces.has("structured")
      ? "mixed"
      : surfaces.has("structured")
        ? "structured"
        : "saas";
  const providers: Record<string, { active: number; total: number }> = {};
  let active = 0;
  let lastSync: string | null = null;
  for (const c of connections) {
    let stat = providers[c.provider];
    if (!stat) {
      stat = { active: 0, total: 0 };
      providers[c.provider] = stat;
    }
    stat.total += 1;
    if (USABLE_CONNECTION_STATUSES.has(c.status)) {
      stat.active += 1;
      active += 1;
    }
    if (c.last_sync_at && (!lastSync || c.last_sync_at > lastSync)) lastSync = c.last_sync_at;
  }
  const total = connections.length;
  const vendors = cat ? [...cat.vendors] : peers.map((c) => c.provider);
  const capability: ConsoleCapability = {
    type: focus.capability,
    label: focus.capability,
    connection_type: cat?.connection_type ?? focus.connection_type,
    surface,
    vendors,
    status: active > 0 ? "connected" : total > 0 ? "error" : "not_connected",
    connections_count: total,
    active_connections: active,
    providers,
    last_sync_at: lastSync,
    needs_reauth: connections.some((c) => c.needs_reauth),
    reconnect: reconnectEntry(focus.capability, vendors[0] ?? focus.provider, focus.surface),
  };
  return {
    capability,
    focused_connection_id: connectionId,
    connections,
    degraded: false,
  };
}

export async function mockConnectConnector(
  type: string,
  vendor?: string,
): Promise<ConsoleConnectResponse> {
  await delay();
  const cat = catalogEntry(type);
  if (!cat) {
    throw new ConsoleMockError("invalid_request", "Tipo di connettore sconosciuto.");
  }
  // A real BE returns the product connect entrypoint. The mock "completes"
  // the flow inline and returns an empty redirect so the page just refetches
  // — clearly divergent from prod on purpose (documented in client.ts).
  const existing = store.connections.filter((c) => c.capability === type);
  const now = new Date().toISOString();
  if (existing.length > 0) {
    // Reconnect: clear the broken state on every connection of the capability.
    for (const conn of existing) {
      conn.needsReauth = false;
      conn.last_used_at = now;
      if (conn.sync && conn.sync.sync_status === "error") {
        conn.sync = { ...conn.sync, sync_status: "completed", sync_error: null };
      }
    }
  } else {
    // New connect: add one healthy connection for the capability.
    const structured = cat.surface === "structured";
    store.connections.push({
      connection_id: `conn_${type}_${Date.now().toString(36)}`,
      capability: type,
      connection_type: cat.connection_type,
      surface: structured ? "structured" : "saas",
      provider: vendor ?? cat.vendors[0] ?? type,
      label: structured ? "Nuova sorgente" : "andrea@example.com",
      scope: structured ? null : "read",
      expires_at: null,
      created_at: now,
      last_used_at: now,
      needsReauth: false,
      ingesting: false,
      sync: structured
        ? null
        : {
            sync_status: "pending",
            sync_error: null,
            last_full_sync_at: null,
            last_incremental_sync_at: null,
            last_sync_at: now,
            items_synced: 0,
            total_chunks: 0,
          },
      schemas: structured
        ? { object_count: 0, record_count: null, last_discovered_at: null, objects: [] }
        : null,
    });
  }
  return {
    redirect_url: "",
    type,
    vendor: vendor ?? cat.vendors[0] ?? null,
    flow: "oauth_redirect",
  };
}

export async function mockSyncConnector(connectionId: string): Promise<void> {
  await delay();
  const conn = store.connections.find((c) => c.connection_id === connectionId);
  if (!conn) {
    throw new ConsoleMockError("not_found", "Connessione non trovata.");
  }
  const now = new Date().toISOString();
  conn.last_used_at = now;
  if (conn.surface === "structured" && conn.schemas) {
    conn.schemas = { ...conn.schemas, last_discovered_at: now };
    conn.ingesting = false;
  } else if (conn.sync) {
    const added = 3 + Math.floor(Math.random() * 20);
    conn.sync = {
      ...conn.sync,
      sync_status: "completed",
      sync_error: null,
      last_incremental_sync_at: now,
      last_sync_at: now,
      items_synced: (conn.sync.items_synced ?? 0) + added,
    };
  }
}

export async function mockDisconnectConnector(connectionId: string): Promise<void> {
  await delay();
  const idx = store.connections.findIndex((c) => c.connection_id === connectionId);
  if (idx === -1) {
    throw new ConsoleMockError("not_found", "Connessione non trovata.");
  }
  store.connections = store.connections.filter((c) => c.connection_id !== connectionId);
}

// ── Custom connectors (developer Push-API) ──────────────────────────────────

export async function mockListCustomConnectors(): Promise<ConsoleCustomConnectorsResponse> {
  await delay();
  const connectors = [...store.customConnectors]
    .sort((a, b) => (b.created_at ?? "").localeCompare(a.created_at ?? ""))
    .map((c) => ({ ...c, config: { ...c.config } }));
  return { connectors, count: connectors.length };
}

export async function mockGetCustomConnector(slug: string): Promise<ConsoleCustomConnector> {
  await delay();
  const found = store.customConnectors.find((c) => c.slug === slug);
  if (!found) {
    throw new ConsoleMockError("not_found", "Connettore non trovato.");
  }
  return { ...found, config: { ...found.config } };
}

export async function mockRebindManifestPrincipal(
  slug: string,
  keyId: string,
): Promise<ConsoleCustomConnector> {
  await delay();
  const connector = store.customConnectors.find((c) => c.slug === slug);
  if (!connector || connector.kind !== "manifest") {
    throw new ConsoleMockError("not_found", "Connettore manifest non trovato.");
  }
  const key = store.keys.find((k) => k.key_id === keyId);
  if (
    !key ||
    key.revoked_at !== null ||
    (key.expires_at !== null && Date.parse(key.expires_at) <= Date.now()) ||
    (key.principal_mode ?? "service") !== "service"
  ) {
    throw new ConsoleMockError("invalid_request", "Seleziona una AppKey server attiva.");
  }
  connector.manifest_principal_key_id = keyId;
  connector.updated_at = new Date().toISOString();
  return { ...connector, config: { ...connector.config } };
}

/**
 * Mirror of the backend ``validate_manifest_config`` + secret custody: checks
 * every rule the server enforces and returns the config as READS carry it —
 * the plaintext ``auth.secret`` stripped, ``secret_set: true`` in its place.
 * ``requireSecret`` is false on PATCH when the row already holds a sealed
 * secret (omitting it keeps the current one).
 */
function validateAndRedactMockManifestConfig(
  config: Record<string, unknown> | null | undefined,
  { requireSecret }: { requireSecret: boolean },
): Record<string, unknown> {
  if (!isPlainObject(config)) {
    throw new ConsoleMockError(
      "invalid_request",
      "manifest connectors require a JSON config object",
    );
  }
  const manifest = config.manifest;
  if (!isPlainObject(manifest)) {
    throw new ConsoleMockError(
      "invalid_request",
      "config.manifest is required for kind='manifest'",
    );
  }
  const baseUrl = manifest.base_url;
  if (typeof baseUrl !== "string" || !baseUrl.startsWith("https://")) {
    throw new ConsoleMockError("invalid_request", "manifest.base_url must be an https:// URL");
  }
  const auth = isPlainObject(manifest.auth) ? manifest.auth : { type: "none" };
  const authType = typeof auth.type === "string" ? auth.type : "none";
  if (!isManifestAuthType(authType)) {
    throw new ConsoleMockError(
      "invalid_request",
      "manifest.auth.type must be one of ['bearer', 'header', 'none']",
    );
  }
  if (
    authType === "header" &&
    !(typeof auth.header_name === "string" && auth.header_name.trim().length > 0)
  ) {
    throw new ConsoleMockError(
      "invalid_request",
      "manifest.auth.header_name is required for type 'header'",
    );
  }
  const hasSecret = typeof auth.secret === "string" && auth.secret.length > 0;
  if (authType !== "none" && !hasSecret && requireSecret) {
    throw new ConsoleMockError(
      "invalid_request",
      "manifest.auth.secret is required for authenticated sources",
    );
  }
  const resource = manifest.resource;
  if (!isPlainObject(resource)) {
    throw new ConsoleMockError("invalid_request", "manifest.resource is required");
  }
  if (typeof resource.path !== "string" || !resource.path.startsWith("/")) {
    throw new ConsoleMockError("invalid_request", "manifest.resource.path must start with '/'");
  }
  const fields = resource.fields;
  if (!isPlainObject(fields) || typeof fields.external_id !== "string") {
    throw new ConsoleMockError(
      "invalid_request",
      "manifest.resource.fields.external_id is required",
    );
  }
  const cursor = resource.cursor;
  if (
    cursor !== undefined &&
    cursor !== null &&
    (!isPlainObject(cursor) ||
      typeof cursor.param !== "string" ||
      typeof cursor.response_field !== "string")
  ) {
    throw new ConsoleMockError(
      "invalid_request",
      "manifest.resource.cursor needs 'param' and 'response_field'",
    );
  }
  const interval = manifest.interval_minutes ?? MANIFEST_DEFAULT_INTERVAL_MINUTES;
  if (
    typeof interval !== "number" ||
    !Number.isInteger(interval) ||
    interval < MANIFEST_MIN_INTERVAL_MINUTES
  ) {
    throw new ConsoleMockError(
      "invalid_request",
      `manifest.interval_minutes must be an integer >= ${MANIFEST_MIN_INTERVAL_MINUTES}`,
    );
  }
  // Redaction: the plaintext secret never survives into the stored row —
  // reads carry ``secret_set: true`` instead (exactly like the backend).
  const redactedAuth: Record<string, unknown> = { type: authType };
  if (authType === "header") redactedAuth.header_name = auth.header_name;
  if (authType !== "none") redactedAuth.secret_set = true;
  return {
    ...config,
    manifest: {
      ...manifest,
      auth: redactedAuth,
      resource: { ...resource, fields: { ...fields } },
      interval_minutes: interval,
    },
  };
}

export async function mockCreateCustomConnector(
  input: ConsoleCustomConnectorCreate,
): Promise<ConsoleCustomConnector> {
  await delay();
  const name = input.name.trim();
  if (name.length === 0) {
    throw new ConsoleMockError("invalid_request", "Il nome del connettore è obbligatorio.");
  }
  const slug = input.slug.trim();
  // Mirror the backend: a malformed OR duplicate slug is a 400, never silently
  // accepted (the slug is the id every runtime sync call addresses).
  if (!isValidCustomConnectorSlug(slug)) {
    throw new ConsoleMockError(
      "invalid_request",
      "Slug non valido: usa lettere minuscole, cifre, punto, trattino o underscore (max 64).",
    );
  }
  if (store.customConnectors.some((c) => c.slug === slug)) {
    throw new ConsoleMockError(
      "invalid_request",
      `Lo slug «${slug}» è già in uso in questo project.`,
    );
  }
  const kind = (input.kind ?? "push").trim() || "push";
  // Mirror the backend ladder: ``code`` is announced but has no runner yet.
  if (kind === "code") {
    throw new ConsoleMockError("invalid_request", "connector kind 'code' is not available yet");
  }
  let config = input.config ? { ...input.config } : {};
  if (kind === "manifest") {
    config = validateAndRedactMockManifestConfig(input.config, { requireSecret: true });
  }
  const now = new Date().toISOString();
  const record: ConsoleCustomConnector = {
    slug,
    name,
    kind,
    active: true,
    config,
    created_at: now,
    updated_at: now,
    last_sync_at: null,
    record_count: 0,
  };
  store.customConnectors = [record, ...store.customConnectors];
  return { ...record, config: { ...record.config } };
}

export async function mockUpdateCustomConnector(
  slug: string,
  patch: ConsoleCustomConnectorUpdate,
): Promise<ConsoleCustomConnector> {
  await delay();
  const found = store.customConnectors.find((c) => c.slug === slug);
  if (!found) {
    throw new ConsoleMockError("not_found", "Connettore non trovato.");
  }
  if (patch.name !== undefined) {
    const name = patch.name.trim();
    if (name.length === 0) {
      throw new ConsoleMockError("invalid_request", "Il nome del connettore è obbligatorio.");
    }
    found.name = name;
  }
  if (patch.config !== undefined) {
    if (found.kind === "manifest") {
      // A fresh ``manifest.auth.secret`` rotates the sealed credential;
      // omitting it keeps the current one — but only if a sealed secret
      // actually exists (``secret_set`` on the stored row).
      const prevManifest = isPlainObject(found.config.manifest) ? found.config.manifest : {};
      const prevAuth = isPlainObject(prevManifest.auth) ? prevManifest.auth : {};
      found.config = validateAndRedactMockManifestConfig(patch.config, {
        requireSecret: prevAuth.secret_set !== true,
      });
    } else {
      if (!isPlainObject(patch.config)) {
        throw new ConsoleMockError(
          "invalid_request",
          "La configurazione deve essere un oggetto JSON.",
        );
      }
      found.config = { ...patch.config };
    }
  }
  if (patch.enabled !== undefined) {
    found.active = patch.enabled;
  }
  found.updated_at = new Date().toISOString();
  return { ...found, config: { ...found.config } };
}

export async function mockDisableCustomConnector(
  slug: string,
): Promise<ConsoleCustomConnectorDisableResponse> {
  await delay();
  const found = store.customConnectors.find((c) => c.slug === slug);
  if (!found) {
    throw new ConsoleMockError("not_found", "Connettore non trovato.");
  }
  // Non-destructive: the row stays (active:false) so already-indexed content
  // remains queryable; only new syncs stop.
  found.active = false;
  found.updated_at = new Date().toISOString();
  return { disabled: true, slug };
}

export async function mockConnectorKinds(): Promise<ConsoleConnectorKindsResponse> {
  await delay();
  // Mirrors the BE ``_CONNECTOR_KIND_CATALOG`` verbatim (the server copy is
  // the source of truth the UI renders): push available, manifest available
  // in PREVIEW, code announced but not yet runnable.
  return {
    kinds: [
      {
        kind: "push",
        available: true,
        title: "Push API",
        summary:
          "You run the connector (cron, Airflow, your own app) and push records; the platform does extraction, chunking, embeddings, ACL, versioning, mark-and-sweep and query filtering.",
        runs: "developer",
        credentials: "none_stored",
        required_scopes: ["sources:manage", "ingest:write"],
      },
      {
        kind: "manifest",
        available: true,
        preview: true,
        title: "Declarative manifest",
        summary:
          "Describe a REST source as a JSON manifest; the platform polls it on a durable schedule. Data, not code — safe to run in-house.",
        runs: "platform",
        credentials: "encrypted_at_rest",
        required_scopes: ["sources:manage"],
      },
      {
        kind: "code",
        available: false,
        preview: true,
        title: "Code connector (sandbox)",
        summary:
          "Write a fetch(config, cursor) generator; the platform runs it in an isolated sandbox with an egress allow-list.",
        runs: "platform_sandbox",
        credentials: "encrypted_at_rest",
        required_scopes: ["sources:manage"],
      },
    ],
    record_contract: {
      fields: [
        {
          name: "external_id",
          type: "string",
          required: true,
          description: "Id stabile del record nel tuo sistema.",
        },
        {
          name: "content",
          type: "string",
          required: false,
          description: "Testo o markdown da indicizzare.",
        },
        { name: "title", type: "string", required: false, description: "Titolo del record." },
        { name: "url", type: "string", required: false, description: "Link canonico al record." },
        {
          name: "metadata",
          type: "object",
          required: false,
          description: "Coppie chiave/valore filtrabili in query.",
        },
      ],
      record_kinds: ["inline", "file_base64", "file_url"],
      reserved_metadata_prefix: "ando_",
      reserved_metadata_keys: ["ando_connector", "ando_external_id"],
      max_records_per_batch: 500,
      max_metadata_keys: 32,
      max_upload_bytes: 26214400,
      supported_file_formats: "PDF, DOCX, XLSX, PPTX, HTML, TXT, MD, PNG, JPEG, WEBP",
      rules: [
        "external_id è obbligatorio e stabile per record.",
        "Almeno uno tra content, file_base64 o file_url.",
        "metadata: oggetto piatto, max 32 chiavi, niente prefisso ando_.",
      ],
    },
  };
}

export async function mockValidateConnectorRecords(
  slug: string,
  records: Array<Record<string, unknown>>,
): Promise<ConsoleRecordValidateResponse> {
  await delay();
  requireConnector(slug);
  const rejected: ConsoleRecordValidateResponse["rejected"] = [];
  const preview: ConsoleRecordValidateResponse["records"] = [];
  records.forEach((rec, index) => {
    const externalId = typeof rec.external_id === "string" ? rec.external_id : "";
    const hasBody = Boolean(rec.content || rec.file_base64 || rec.file_url);
    if (!externalId) {
      rejected.push({ index, field: "external_id", reason: "external_id obbligatorio" });
    } else if (!hasBody) {
      rejected.push({ index, field: "content", reason: "serve content, file_base64 o file_url" });
    } else {
      const metaKeys = Object.keys((rec.metadata as Record<string, unknown>) ?? {});
      preview.push({
        index,
        external_id: externalId,
        kind: rec.file_url ? "file_url" : rec.file_base64 ? "file_base64" : "inline",
        body: rec.file_url ? "file_url" : rec.file_base64 ? "file_base64" : "inline",
        content_type: null,
        metadata_keys: [...metaKeys, "ando_connector"],
        acl: "project_service_user",
        content_hash: "mock-content-hash",
      });
    }
  });
  return { accepted: preview.length, rejected, records: preview };
}

export async function mockConnectorSyncs(
  slug: string,
  limit: number,
): Promise<ConsoleSyncListResponse> {
  await delay();
  requireConnector(slug);
  const now = Date.now();
  const syncs: ConsoleSyncListResponse["syncs"] = [
    {
      sync_id: "sync-2",
      mode: "incremental",
      status: "completed",
      indexed: 12,
      deleted: 0,
      unchanged: 40,
      rejected: 1,
      duration_ms: 4200,
      started_at: new Date(now - 3_600_000).toISOString(),
      completed_at: new Date(now - 3_595_800).toISOString(),
    },
    {
      sync_id: "sync-1",
      mode: "full",
      status: "completed",
      indexed: 52,
      deleted: 0,
      unchanged: 0,
      rejected: 0,
      duration_ms: 15_000,
      started_at: new Date(now - 86_400_000).toISOString(),
      completed_at: new Date(now - 86_385_000).toISOString(),
    },
  ].slice(0, limit);
  return { connector: slug, syncs, count: syncs.length };
}

// ── Custom-connector actions (the write-loop) ────────────────────────────────

function requireConnector(slug: string): ConsoleCustomConnector {
  const found = store.customConnectors.find((c) => c.slug === slug);
  if (!found) {
    throw new ConsoleMockError("not_found", "Connettore non trovato.");
  }
  return found;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** JSON-Schema ``type`` name for a runtime value (integer distinguished). */
function jsonSchemaType(value: unknown): string {
  if (typeof value === "string") return "string";
  if (typeof value === "boolean") return "boolean";
  if (typeof value === "number") return Number.isInteger(value) ? "integer" : "number";
  if (Array.isArray(value)) return "array";
  if (isPlainObject(value)) return "object";
  return "null";
}

/**
 * Minimal params ⇄ schema check mirroring the backend's 400 on bad params —
 * NOT a full Draft 2020-12 validator: enough to reject the everyday mistakes
 * (missing required key, wrong scalar type, unexpected key). An empty /
 * unconstrained schema accepts anything.
 */
function validateActionParams(
  schema: Record<string, unknown>,
  params: Record<string, unknown>,
): { ok: true } | { ok: false; message: string } {
  const properties = isPlainObject(schema.properties) ? schema.properties : {};
  const required = Array.isArray(schema.required)
    ? schema.required.filter((k): k is string => typeof k === "string")
    : [];

  for (const key of required) {
    if (!(key in params)) {
      return { ok: false, message: `Parametro obbligatorio mancante: '${key}'.` };
    }
  }
  if (schema.additionalProperties === false) {
    for (const key of Object.keys(params)) {
      if (!(key in properties)) {
        return { ok: false, message: `Parametro non previsto dallo schema: '${key}'.` };
      }
    }
  }
  for (const [key, value] of Object.entries(params)) {
    const prop = properties[key];
    if (!isPlainObject(prop) || typeof prop.type !== "string") continue;
    const actual = jsonSchemaType(value);
    const expected = prop.type;
    const compatible = actual === expected || (expected === "number" && actual === "integer");
    if (!compatible) {
      return {
        ok: false,
        message: `Il parametro '${key}' deve essere di tipo '${expected}' (ricevuto '${actual}').`,
      };
    }
  }
  return { ok: true };
}

function findActionRequest(id: string): ConsoleActionRequest {
  const found = store.actionRequests.find((r) => r.id === id);
  if (!found) {
    throw new ConsoleMockError("not_found", "Richiesta di azione non trovata.");
  }
  return found;
}

export async function mockListConnectorActions(
  slug: string,
): Promise<ConsoleActionDefinitionsResponse> {
  await delay();
  requireConnector(slug);
  const actions = (store.actionDefs.get(slug) ?? [])
    .map((a) => ({ ...a, params_schema: { ...a.params_schema } }))
    .sort((a, b) => a.name.localeCompare(b.name));
  return { actions, count: actions.length };
}

export async function mockDeclareConnectorAction(
  slug: string,
  input: ConsoleActionDefinitionCreate,
): Promise<ConsoleActionDefinition> {
  await delay();
  requireConnector(slug);
  const name = input.name.trim();
  if (!isValidConnectorActionName(name)) {
    throw new ConsoleMockError(
      "invalid_request",
      "Nome azione non valido: usa lettere minuscole, cifre, punto, trattino o underscore (max 64).",
    );
  }
  const existing = store.actionDefs.get(slug) ?? [];
  if (existing.some((a) => a.name === name)) {
    throw new ConsoleMockError(
      "invalid_request",
      `L'azione «${name}» è già dichiarata per questo connettore.`,
    );
  }
  if (input.params_schema !== undefined && !isPlainObject(input.params_schema)) {
    throw new ConsoleMockError(
      "invalid_request",
      "Lo schema dei parametri deve essere un oggetto JSON Schema.",
    );
  }
  const risk = input.risk ?? "low";
  if (!isConsoleActionRisk(risk)) {
    throw new ConsoleMockError(
      "invalid_request",
      "Livello di rischio non valido: usa low, medium, high o critical.",
    );
  }
  const now = new Date().toISOString();
  const record: ConsoleActionDefinition = {
    connector: slug,
    name,
    description: input.description?.trim() ? input.description.trim() : null,
    params_schema: input.params_schema ? { ...input.params_schema } : {},
    risk,
    binding: input.binding ? { ...input.binding } : null,
    created_at: now,
    updated_at: now,
  };
  store.actionDefs.set(slug, [...existing, record]);
  return { ...record, params_schema: { ...record.params_schema } };
}

export async function mockDeleteConnectorAction(
  slug: string,
  name: string,
): Promise<ConsoleActionDefinitionDeleteResponse> {
  await delay();
  requireConnector(slug);
  const existing = store.actionDefs.get(slug) ?? [];
  if (!existing.some((a) => a.name === name)) {
    throw new ConsoleMockError("not_found", "Azione non trovata.");
  }
  store.actionDefs.set(
    slug,
    existing.filter((a) => a.name !== name),
  );
  return { deleted: true, connector: slug, name };
}

/** ``POST /custom-connectors/{slug}/actions/{name}/invoke`` — synchronous
 * HTTP-bound run: schema-validated params, the same high/critical
 * ``confirm_risk`` gate as execute, then a simulated bound-endpoint reply. */
export async function mockInvokeConnectorAction(
  slug: string,
  name: string,
  body?: ConsoleActionInvokeRequest,
): Promise<ConsoleActionInvokeResponse> {
  await delay();
  requireConnector(slug);
  const def = (store.actionDefs.get(slug) ?? []).find((a) => a.name === name);
  if (!def) {
    throw new ConsoleMockError("not_found", `L'azione «${name}» non è dichiarata per «${slug}».`);
  }
  if (!def.binding) {
    throw new ConsoleMockError(
      "invalid_request",
      `L'azione «${name}» non ha un binding HTTP: usa il flusso proponi → esegui.`,
    );
  }
  const params = body?.params ?? {};
  const validation = validateActionParams(def.params_schema, params);
  if (!validation.ok) {
    throw new ConsoleMockError("invalid_request", validation.message);
  }
  if (actionRiskRequiresConfirm(def.risk) && body?.confirm_risk !== true) {
    throw new ConsoleMockError(
      "invalid_request",
      `L'azione è a rischio «${def.risk}»: serve confirm_risk per invocarla.`,
    );
  }
  return {
    ok: true,
    status_code: 200,
    response: { received: true, action: name, params: { ...params } },
  };
}

export async function mockProposeAction(
  input: ConsoleActionRequestCreate,
): Promise<ConsoleActionRequest> {
  await delay();
  requireConnector(input.connector);
  const def = (store.actionDefs.get(input.connector) ?? []).find((a) => a.name === input.action);
  if (!def) {
    throw new ConsoleMockError(
      "not_found",
      `L'azione «${input.action}» non è dichiarata per «${input.connector}».`,
    );
  }
  const params = input.params ?? {};
  if (!isPlainObject(params)) {
    throw new ConsoleMockError("invalid_request", "I parametri devono essere un oggetto JSON.");
  }
  const validation = validateActionParams(def.params_schema, params);
  if (!validation.ok) {
    throw new ConsoleMockError("invalid_request", validation.message);
  }
  store.actionSeq += 1;
  const now = new Date().toISOString();
  const request: ConsoleActionRequest = {
    id: `act_req_${store.actionSeq}_${crypto.randomUUID().slice(0, 8)}`,
    connector: input.connector,
    action: def.name,
    params: { ...params },
    status: "proposed",
    risk: def.risk,
    preview: {
      summary: `Verrà inviata l'azione «${def.name}» al tuo applicativo «${input.connector}».`,
      connector: input.connector,
      action: def.name,
      risk: def.risk,
      params: { ...params },
      requires_confirmation: actionRiskRequiresConfirm(def.risk),
    },
    result: null,
    error: null,
    created_at: now,
    updated_at: now,
    dispatched_at: null,
    completed_at: null,
  };
  store.actionRequests = [request, ...store.actionRequests];
  return { ...request, params: { ...request.params }, preview: { ...request.preview } };
}

export async function mockExecuteAction(
  id: string,
  body?: ConsoleActionExecuteRequest,
): Promise<ConsoleActionRequest> {
  await delay();
  const request = findActionRequest(id);
  if (request.status !== "proposed") {
    throw new ConsoleMockError(
      "conflict",
      `La richiesta è nello stato «${request.status}»: solo una richiesta «proposed» può essere eseguita.`,
    );
  }
  if (actionRiskRequiresConfirm(request.risk) && body?.confirm_risk !== true) {
    throw new ConsoleMockError(
      "invalid_request",
      `L'azione è a rischio «${request.risk}»: serve confirm_risk per eseguirla.`,
    );
  }
  const now = new Date().toISOString();
  // A project-wide endpoint subscribed to ``action.requested`` (the console
  // Webhooks surface) closes the loop for EVERY connector — mirror that here
  // on top of the legacy per-connector seed set.
  const hasActionWebhook =
    store.actionWebhooks.has(request.connector) ||
    store.webhooks.some((w) => w.active && w.events.includes("action.requested"));
  // No webhook subscribed → the request fails and the API explains how to fix it.
  if (!hasActionWebhook) {
    request.status = "failed";
    request.updated_at = now;
    request.preview = null;
    request.error =
      "Nessun webhook sottoscritto all'evento 'action.requested' per questo connettore: registra un webhook per ricevere ed eseguire l'azione.";
    throw new ConsoleMockError("invalid_request", request.error);
  }
  request.status = "dispatched";
  request.dispatched_at = now;
  request.updated_at = now;
  request.preview = null;
  request.error = null;
  return { ...request, params: { ...request.params } };
}

export async function mockGetActionRequest(id: string): Promise<ConsoleActionRequest> {
  await delay();
  const request = findActionRequest(id);
  return {
    ...request,
    params: { ...request.params },
    preview: request.preview ? { ...request.preview } : null,
    result: request.result ? { ...request.result } : null,
  };
}

export async function mockListActionRequests(
  slug: string,
  limit = 20,
): Promise<ConsoleActionRequestsResponse> {
  await delay();
  requireConnector(slug);
  const all = store.actionRequests.filter((r) => r.connector === slug);
  const actions = all.slice(0, Math.max(1, limit)).map((r) => ({
    ...r,
    params: { ...r.params },
    preview: r.preview ? { ...r.preview } : null,
    result: r.result ? { ...r.result } : null,
  }));
  return { actions, count: all.length };
}

// ── Webhooks (console session projection) ───────────────────────────────────

export async function mockCreateWebhook(
  input: ConsoleWebhookCreate,
): Promise<ConsoleWebhookCreated> {
  await delay();
  const url = input.url.trim();
  if (!url.startsWith("https://")) {
    throw new ConsoleMockError("invalid_request", "webhook url must be an https:// URL");
  }
  const events = [...new Set(input.events)];
  const valid = CONSOLE_WEBHOOK_EVENTS as readonly string[];
  const unknown = events.filter((e) => !valid.includes(e));
  if (events.length === 0 || unknown.length > 0) {
    throw new ConsoleMockError(
      "invalid_request",
      `events must be a non-empty subset of: ${valid.join(", ")}`,
    );
  }
  store.webhookSeq += 1;
  const record: ConsoleWebhookSummary = {
    webhook_id: `wh_${store.webhookSeq}_${crypto.randomUUID().slice(0, 8)}`,
    url,
    events,
    active: true,
    created_at: new Date().toISOString(),
  };
  store.webhooks = [record, ...store.webhooks];
  // The signing secret appears EXACTLY ONCE — the store never keeps it.
  return {
    ...record,
    events: [...record.events],
    secret: `whsec_${crypto.randomUUID().replaceAll("-", "")}`,
  };
}

export async function mockListWebhooks(): Promise<ConsoleWebhooksResponse> {
  await delay();
  const webhooks = store.webhooks.map((w) => ({ ...w, events: [...w.events] }));
  return { webhooks, count: webhooks.length };
}

export async function mockDeleteWebhook(webhookId: string): Promise<ConsoleWebhookDeleted> {
  await delay();
  if (!store.webhooks.some((w) => w.webhook_id === webhookId)) {
    throw new ConsoleMockError("not_found", "Webhook non trovato.");
  }
  store.webhooks = store.webhooks.filter((w) => w.webhook_id !== webhookId);
  return { deleted: true, webhook_id: webhookId };
}

export async function mockTestWebhook(webhookId: string): Promise<ConsoleWebhookTestResult> {
  await delay();
  const found = store.webhooks.find((w) => w.webhook_id === webhookId);
  if (!found) {
    throw new ConsoleMockError("not_found", "Webhook non trovato.");
  }
  // A receiver that fails is a 200 with delivered:false (never an API error);
  // a URL containing "fail" simulates it so the UI state is exercisable.
  if (found.url.includes("fail")) {
    return {
      webhook_id: webhookId,
      event: "test.ping",
      delivered: false,
      http_status: 500,
      error: "Il ricevitore ha risposto 500 al test.ping.",
    };
  }
  return {
    webhook_id: webhookId,
    event: "test.ping",
    delivered: true,
    http_status: 200,
    error: null,
  };
}

// ── Webhook deliveries (history + replay) ───────────────────────────────────
//
// State lives OUTSIDE ``MockStore`` (append-only seam): a lazy per-webhook
// map KEYED to the current store instance, so ``resetConsoleMockStore()``
// still restores pristine fixtures without touching the store shape.

let deliveriesOwner: MockStore | null = null;
let deliveriesByWebhook = new Map<string, ConsoleWebhookDelivery[]>();

/** Only the seeded endpoint has history out of the box — an endpoint created
 * in-session starts EMPTY (the UI's honest "appears on the first event"). */
function seedWebhookDeliveries(webhookId: string): ConsoleWebhookDelivery[] {
  if (webhookId !== "wh_seed_1") return [];
  return [
    {
      delivery_id: "whd_seed_3",
      event: "source.sync.completed",
      status: "pending",
      attempts: 1,
      payload: { event: "source.sync.completed", connector: "acme-crm", records: 128 },
      next_attempt_at: new Date(Date.now() + 60_000).toISOString(),
      created_at: isoDaysAgo(0, 1),
      delivered_at: null,
    },
    {
      delivery_id: "whd_seed_2",
      event: "ingestion.completed",
      status: "failed",
      attempts: 5,
      payload: { event: "ingestion.completed", document_id: "doc_9f2", chunks: 12 },
      next_attempt_at: null,
      created_at: isoDaysAgo(1),
      delivered_at: null,
    },
    {
      delivery_id: "whd_seed_1",
      event: "ingestion.completed",
      status: "delivered",
      attempts: 1,
      payload: { event: "ingestion.completed", document_id: "doc_4c7", chunks: 31 },
      next_attempt_at: null,
      created_at: isoDaysAgo(2),
      delivered_at: isoDaysAgo(2),
    },
  ];
}

function webhookDeliveriesFor(webhookId: string): ConsoleWebhookDelivery[] {
  if (deliveriesOwner !== store) {
    deliveriesOwner = store;
    deliveriesByWebhook = new Map();
  }
  let list = deliveriesByWebhook.get(webhookId);
  if (!list) {
    list = seedWebhookDeliveries(webhookId);
    deliveriesByWebhook.set(webhookId, list);
  }
  return list;
}

export async function mockGetWebhookDeliveries(
  webhookId: string,
): Promise<ConsoleWebhookDeliveriesResponse> {
  await delay();
  if (!store.webhooks.some((w) => w.webhook_id === webhookId)) {
    throw new ConsoleMockError("not_found", "Webhook non trovato.");
  }
  const deliveries = webhookDeliveriesFor(webhookId).map((d) => ({
    ...d,
    payload: { ...d.payload },
  }));
  return { deliveries, count: deliveries.length };
}

export async function mockReplayWebhookDelivery(
  deliveryId: string,
): Promise<ConsoleWebhookDeliveryReplayed> {
  await delay();
  // Materialize every known endpoint's history before searching by id.
  for (const w of store.webhooks) webhookDeliveriesFor(w.webhook_id);
  for (const list of deliveriesByWebhook.values()) {
    const found = list.find((d) => d.delivery_id === deliveryId);
    if (!found) continue;
    // Replay works on ANY status (delivered included): back in the queue
    // with an immediate retry and a fresh signature.
    found.status = "pending";
    found.attempts = 0;
    found.next_attempt_at = new Date().toISOString();
    found.delivered_at = null;
    return {
      delivery_id: found.delivery_id,
      event: found.event,
      status: "pending",
      attempts: found.attempts,
    };
  }
  throw new ConsoleMockError("not_found", "Consegna non trovata.");
}

/** Uploaded (newest first) + fixtures, minus deletions — the "index". */
function listAllDocuments(): ConsoleDocument[] {
  const base = Array.from({ length: TOTAL_DOCUMENTS }, (_, i) => buildDocument(i));
  return [...store.uploadedDocs, ...base].filter((d) => !store.deletedDocIds.has(d.id));
}

export async function mockListDocuments(page: number): Promise<ConsoleDocumentsPage> {
  await delay();
  const all = listAllDocuments();
  const safePage = Math.max(1, Math.floor(page));
  const start = (safePage - 1) * PAGE_SIZE;
  return {
    documents: all.slice(start, start + PAGE_SIZE),
    page: safePage,
    page_size: PAGE_SIZE,
    total: all.length,
  };
}

export async function mockUploadDocument(
  name: string,
  sizeBytes: number,
): Promise<ConsoleDocument> {
  await delay();
  // Same constraints the page enforces client-side — the mock mirrors the
  // BE so direct client calls fail loudly too.
  const validation = validateUploadFile({ name, size: sizeBytes });
  if (!validation.ok) {
    throw new ConsoleMockError(
      "invalid_request",
      validation.rejection.kind === "size"
        ? "Il file supera il limite di 25 MB."
        : "Formato non supportato: sono ammessi PDF, DOCX, TXT e MD.",
    );
  }
  store.docSeq += 1;
  const doc: ConsoleDocument = {
    id: `doc_up_${store.docSeq}`,
    name,
    source: "upload",
    chunks: 0,
    size_bytes: sizeBytes,
    status: "running",
    created_at: new Date().toISOString(),
  };
  store.uploadedDocs = [doc, ...store.uploadedDocs];
  return { ...doc };
}

export async function mockDeleteDocument(documentId: string): Promise<void> {
  await delay();
  const exists = listAllDocuments().some((d) => d.id === documentId);
  if (!exists) {
    throw new ConsoleMockError("not_found", "Documento non trovato.");
  }
  store.deletedDocIds.add(documentId);
}

function isoBefore(iso: string, days: number): string {
  const d = new Date(iso);
  d.setDate(d.getDate() - days);
  return d.toISOString();
}

export async function mockDocumentJobs(documentId: string): Promise<ConsoleJobsResponse> {
  await delay();
  const doc = listAllDocuments().find((d) => d.id === documentId);
  if (!doc) {
    throw new ConsoleMockError("not_found", "Documento non trovato.");
  }
  // Newest first: the run matching the current status, plus a plausible
  // deterministic history for fixture docs (re-index / first ingestion).
  const jobs: ConsoleRecentJob[] = [
    {
      id: `job_${doc.id}_latest`,
      status: doc.status,
      document: doc.name,
      created_at: doc.created_at,
    },
  ];
  if (doc.status === "failed" || doc.chunks > 80) {
    jobs.push({
      id: `job_${doc.id}_prev`,
      status: "completed",
      document: doc.name,
      created_at: isoBefore(doc.created_at, 7),
    });
  }
  return { jobs };
}

export async function mockListJobs(status?: ConsoleJobStatus): Promise<ConsoleJobsResponse> {
  await delay();
  const jobs: ConsoleRecentJob[] = Array.from({ length: 20 }, (_, i) => {
    const doc = buildDocument(i);
    return {
      id: `job_${700 - i}`,
      status: doc.status,
      document: doc.name,
      created_at: isoDaysAgo(Math.floor(i / 4), i),
    };
  });
  return { jobs: status ? jobs.filter((j) => j.status === status) : jobs };
}

export async function mockUsage(window: ConsoleUsageWindow = "30d"): Promise<ConsoleUsage> {
  await delay();
  return buildUsage(CONSOLE_USAGE_WINDOW_DAYS[window]);
}

export async function mockUsageBreakdown(
  window: ConsoleUsageWindow = "30d",
): Promise<ConsoleUsageBreakdown> {
  await delay();
  return buildUsageBreakdown(window);
}

export async function mockRunQuery(input: ConsoleQueryRequest): Promise<ConsoleQueryResponse> {
  await delay();
  await delay();
  const query = input.query.trim();
  if (query.length === 0) {
    throw new ConsoleMockError("invalid_request", "La query non può essere vuota.");
  }
  const k = Math.max(1, Math.min(20, input.k));
  const results = Array.from({ length: k }, (_, i) => {
    const doc = buildDocument(i * 5 + (query.length % 7));
    return {
      id: `chunk_${doc.id}_${i}`,
      document: doc.name,
      document_id: doc.id,
      source: doc.source,
      snippet: `… passaggio rilevante per «${query}» (sezione ${i + 1}): il documento descrive le condizioni applicabili e i riferimenti operativi collegati …`,
      score: Number((0.93 - i * 0.055).toFixed(3)),
    };
  });
  const citations = results.slice(0, Math.min(3, results.length)).map((r, i) => ({
    index: i + 1,
    document: r.document,
    chunk_id: r.id,
    snippet: r.snippet,
  }));
  return {
    mode: input.mode,
    answer:
      input.mode === "answer"
        ? `Risposta simulata per «${query}»: in base ai documenti indicizzati, la richiesta trova riscontro nei passaggi citati [1][2]. Questa è una risposta generata dal layer mock della console — il backend runtime arriva nel prossimo round.`
        : null,
    results,
    citations: input.mode === "answer" ? citations : [],
  };
}

// ── GET /sources — scope discovery (playground) ────────────────────────────
//
// Derived from the raw connections, exactly like the real
// ``platform_sources_service``: unstructured surfaces collapse to their
// source id (drive/email/chat) with document/chunk counts; each structured
// connection is a ``conn_<id>`` entry carrying its WHITE-LABEL capability.
// Never a vendor slug.

/** Capability → the unstructured source id + white-label label it publishes. */
const UNSTRUCTURED_SOURCE_META: Record<string, { id: string; label: string }> = {
  email: { id: "email", label: "Posta" },
  drive: { id: "drive", label: "Documenti cloud" },
  messaging: { id: "chat", label: "Chat aziendale" },
};

/** Fallback white-label label per structured capability (the vendor is never shown). */
const STRUCTURED_SOURCE_LABELS: Record<string, string> = {
  database: "Database (SQL)",
  spreadsheet: "Foglio di calcolo",
  erp: "ERP",
};

export async function mockListSources(asKeyId?: string): Promise<ConsoleSourcesResponse> {
  await delay();
  const key = asKeyId ? store.keys.find((candidate) => candidate.key_id === asKeyId) : null;
  if (
    asKeyId &&
    (!key ||
      key.revoked_at ||
      (key.expires_at && Date.parse(key.expires_at) <= Date.now()) ||
      (key.principal_mode && key.principal_mode !== "service"))
  ) {
    throw new ConsoleMockError("not_found", "AppKey non disponibile.");
  }
  const entries: ConsoleSourceEntry[] = [];

  // Unstructured: one entry per source id, aggregated across its accounts.
  const unstructured = new Map<string, ConsoleSourceEntry>();
  for (const conn of store.connections) {
    const meta = UNSTRUCTURED_SOURCE_META[conn.capability];
    if (!meta || conn.surface !== "saas") continue;
    const done = conn.sync?.sync_status === "completed";
    const items = conn.sync?.items_synced ?? 0;
    const chunks = conn.sync?.total_chunks ?? 0;
    const existing = unstructured.get(meta.id);
    if (existing) {
      existing.documents = (existing.documents ?? 0) + items;
      existing.chunks = (existing.chunks ?? 0) + chunks;
      existing.queryable = existing.queryable || done;
      if (existing.queryable) existing.reason = null;
      const ls = connectionLastSyncAt(conn);
      if (ls && (!existing.last_sync_at || ls > existing.last_sync_at)) existing.last_sync_at = ls;
    } else {
      unstructured.set(meta.id, {
        id: meta.id,
        kind: "unstructured",
        label: meta.label,
        documents: items,
        chunks,
        last_sync_at: connectionLastSyncAt(conn),
        queryable: done,
        reason: done ? null : "Prima sincronizzazione non ancora completata",
      });
    }
  }
  entries.push(...unstructured.values());

  // Structured: one entry per connection, id = its conn_<id>. Mirrors the new
  // backend contract: ``label`` is the white-label CAPABILITY (the map's
  // grouping dimension), ``name`` is the distinguishing display name (alias ▸
  // connection label ▸ capability), with ``object_count`` + ``state``.
  for (const conn of store.connections) {
    if (conn.surface !== "structured") continue;
    const objectCount = conn.schemas?.object_count ?? 0;
    const discovered = objectCount > 0 && !conn.ingesting;
    // ``ready`` once a schema is discovered (TRAMA can query it); no publish
    // gate — the legacy flag never affected retrieval.
    const state: ConsoleSourceState = discovered ? "ready" : "pending";
    entries.push({
      id: conn.connection_id,
      kind: "structured",
      label: STRUCTURED_SOURCE_LABELS[conn.capability] || conn.capability,
      name:
        store.sourceAlias.get(conn.connection_id) ||
        conn.label ||
        STRUCTURED_SOURCE_LABELS[conn.capability] ||
        conn.capability,
      connector_type: conn.capability,
      object_count: objectCount,
      state,
      last_sync_at: conn.schemas?.last_discovered_at ?? null,
      queryable: discovered,
      reason: discovered ? null : "Prima sincronizzazione non ancora completata",
    });
  }

  const policy = key?.resource_policy;
  const visible = entries.filter((entry) => {
    if (entry.kind === "structured") {
      return (
        !policy?.connector_ids || policy.connector_ids.includes(entry.id.replace(/^conn_/, ""))
      );
    }
    return !policy?.sources || policy.sources.includes(entry.id);
  });
  visible.sort((a, b) => a.id.localeCompare(b.id));
  return {
    sources: visible,
    total: visible.length,
    count: visible.length,
    has_more: false,
    next_cursor: null,
  };
}

// ── GET /uks — the real (structure-only) UKS facet forest ──────────────────

export async function mockGetUks(): Promise<ConsoleUksResponse> {
  await delay();
  const facets: ConsoleUksResponse["facets"] = [
    {
      id: "facet:src:drive",
      name: "Documenti cloud",
      path: "@drive",
      level: "source",
      docs: 1284,
      chunks: 9140,
      digest: "Contratti, offerte e documentazione tecnica.",
      doc_types: ["pdf", "docx"],
    },
    {
      id: "facet:src:email",
      name: "Email",
      path: "@email",
      level: "source",
      docs: 512,
      chunks: 3010,
      digest: "Corrispondenza commerciale e ordini.",
      doc_types: ["eml"],
    },
    {
      id: "facet:path:/Vendite",
      name: "Vendite",
      path: "/Vendite",
      level: "folder",
      docs: 420,
      chunks: 3200,
      digest: "Offerte, listini e ordini clienti.",
      doc_types: ["pdf", "xlsx"],
    },
    {
      id: "facet:path:/Vendite/2026",
      name: "2026",
      path: "/Vendite/2026",
      level: "folder",
      docs: 180,
      chunks: 1400,
      digest: "Preventivi e ordini dell'anno in corso.",
      doc_types: ["pdf"],
    },
    {
      id: "facet:path:/Vendite/Listini",
      name: "Listini",
      path: "/Vendite/Listini",
      level: "folder",
      docs: 24,
      chunks: 190,
      digest: "Listini prezzi per canale.",
      doc_types: ["xlsx"],
    },
    {
      id: "facet:path:/Tecnico",
      name: "Tecnico",
      path: "/Tecnico",
      level: "folder",
      docs: 640,
      chunks: 5100,
      digest: "Disegni, distinte e specifiche di prodotto.",
      doc_types: ["pdf", "dxf"],
    },
    {
      id: "facet:path:/Tecnico/DXF",
      name: "DXF",
      path: "/Tecnico/DXF",
      level: "folder",
      docs: 300,
      chunks: 2200,
      digest: "Cartella tecnica in formato DXF.",
      doc_types: ["dxf"],
    },
  ];
  const edges: ConsoleUksResponse["edges"] = [
    { src: "facet:path:/Vendite", dst: "facet:path:/Vendite/2026" },
    { src: "facet:path:/Vendite", dst: "facet:path:/Vendite/Listini" },
    { src: "facet:path:/Tecnico", dst: "facet:path:/Tecnico/DXF" },
  ];
  return { materialized: true, profiled_at: isoDaysAgo(0), facets, edges };
}

// ── GET/PATCH/POST /sources/{id}/schema — K-Schema curation ────────────────
//
// Mirrors ``platform_schema_curation_service``: the profiled schema of ONE
// structured connection, the human curation overrides (``user_edited``) and
// the PUBLISH gate (``queryable=false, reason="schema non pubblicato"`` until
// published). Publish + curation state live in the store so they reset between
// tests and stick within a session.

/** The structured connection a source id addresses, or undefined. */
function structuredConnection(sourceId: string): MockConnection | undefined {
  return store.connections.find((c) => c.connection_id === sourceId && c.surface === "structured");
}

/** Deterministic column set for a profiled object (name → plausible columns). */
function schemaColumnsFor(objectName: string): ReadonlyArray<{ name: string; data_type: string }> {
  const base = [
    { name: "id", data_type: "integer" },
    { name: "codice", data_type: "text" },
    { name: "descrizione", data_type: "text" },
    { name: "importo", data_type: "numeric" },
    { name: "aggiornato_il", data_type: "timestamp" },
  ];
  // Vary the count by name length so different objects differ.
  return base.slice(0, 3 + (objectName.length % 3));
}

/** The profiler's AI-proposed description for an object (pre-curation). */
function aiProposedDescription(objectName: string): string {
  return `Tabella «${objectName}» rilevata e descritta automaticamente dal profiler.`;
}

/** Compose the object view from the raw object + any human override. */
function schemaObjectView(
  objectName: string,
  edit: ConsoleSchemaObjectCuration | undefined,
  lastDiscoveredAt: string | null,
): ConsoleSchemaObjectView {
  const columns = schemaColumnsFor(objectName).map((c) => ({
    name: c.name,
    data_type: c.data_type,
    description: edit?.column_descriptions?.[c.name] ?? null,
  }));
  return {
    object: objectName,
    description: edit?.description ?? aiProposedDescription(objectName),
    columns,
    semantic_mappings: edit?.semantic_mappings ?? [],
    user_edited: edit !== undefined,
    updated_at: edit ? isoDaysAgo(0) : lastDiscoveredAt,
  };
}

/** Layer the publish gate on top of the base queryable/reason (like the BE). */
function schemaStatus(conn: MockConnection, sourceId: string): ConsoleSchemaStatus {
  const publishedAt = store.schemaPublished.get(sourceId) ?? null;
  const published = publishedAt !== null;
  const baseQueryable = (conn.schemas?.object_count ?? 0) > 0 && !conn.ingesting;
  const baseReason = baseQueryable ? null : "Prima sincronizzazione non ancora completata";
  const queryable = baseQueryable && published;
  const reason = baseReason ?? (published ? null : "schema non pubblicato");
  return {
    id: sourceId,
    connector_type: conn.capability,
    label: conn.label,
    last_sync_at: conn.schemas?.last_discovered_at ?? null,
    published,
    published_at: publishedAt,
    queryable,
    reason,
  };
}

export async function mockGetSourceSchema(sourceId: string): Promise<ConsoleSchemaResponse> {
  await delay();
  const conn = structuredConnection(sourceId);
  if (!conn) {
    throw new ConsoleMockError("not_found", `Nessuna sorgente strutturata con id '${sourceId}'.`);
  }
  const curation = store.schemaCuration.get(sourceId);
  const objects = (conn.schemas?.objects ?? []).map((obj) =>
    schemaObjectView(obj.name, curation?.get(obj.name), conn.schemas?.last_discovered_at ?? null),
  );
  return { source: schemaStatus(conn, sourceId), objects, count: objects.length };
}

export async function mockCurateSchemaObject(
  sourceId: string,
  objectName: string,
  body: ConsoleSchemaObjectCuration,
): Promise<ConsoleSchemaObjectView> {
  await delay();
  if (
    body.description === undefined &&
    body.column_descriptions === undefined &&
    body.semantic_mappings === undefined
  ) {
    throw new ConsoleMockError(
      "invalid_request",
      "Fornisci almeno un campo tra descrizione, descrizioni colonne o mappings.",
    );
  }
  const conn = structuredConnection(sourceId);
  const exists = conn?.schemas?.objects.some((o) => o.name === objectName);
  if (!conn || !exists) {
    throw new ConsoleMockError(
      "not_found",
      `La sorgente '${sourceId}' non ha l'oggetto '${objectName}'.`,
    );
  }
  let bySource = store.schemaCuration.get(sourceId);
  if (!bySource) {
    bySource = new Map<string, ConsoleSchemaObjectCuration>();
    store.schemaCuration.set(sourceId, bySource);
  }
  const prev = bySource.get(objectName) ?? {};
  const merged: ConsoleSchemaObjectCuration = {
    description: body.description ?? prev.description,
    column_descriptions: {
      ...(prev.column_descriptions ?? {}),
      ...(body.column_descriptions ?? {}),
    },
    semantic_mappings: body.semantic_mappings ?? prev.semantic_mappings,
  };
  bySource.set(objectName, merged);
  return schemaObjectView(objectName, merged, conn.schemas?.last_discovered_at ?? null);
}

export async function mockRenameSource(
  sourceId: string,
  name: string,
): Promise<ConsoleRenamedSource> {
  await delay();
  const conn = structuredConnection(sourceId);
  if (!conn) {
    throw new ConsoleMockError("not_found", `Nessuna sorgente strutturata con id '${sourceId}'.`);
  }
  const trimmed = name.trim();
  if (trimmed) store.sourceAlias.set(sourceId, trimmed);
  else store.sourceAlias.delete(sourceId);
  const resolved =
    trimmed || conn.label || STRUCTURED_SOURCE_LABELS[conn.capability] || conn.capability;
  return { id: sourceId, name: resolved };
}

export async function mockPublishSourceSchema(
  sourceId: string,
  published: boolean,
): Promise<ConsoleSchemaStatus> {
  await delay();
  const conn = structuredConnection(sourceId);
  if (!conn) {
    throw new ConsoleMockError("not_found", `Nessuna sorgente strutturata con id '${sourceId}'.`);
  }
  if (published && (conn.schemas?.object_count ?? 0) === 0) {
    throw new ConsoleMockError(
      "conflict",
      `La sorgente '${sourceId}' non ha ancora uno schema da pubblicare.`,
    );
  }
  if (published) store.schemaPublished.set(sourceId, isoDaysAgo(0));
  else store.schemaPublished.delete(sourceId);
  return schemaStatus(conn, sourceId);
}

// ── POST /agentic/answer — the full agentic stack, read-only (playground) ──
//
// Deterministic given its inputs (no randomness) so the UI + tests are
// stable. Every field is WHITE-LABEL: capabilities are `database` /
// `spreadsheet`, buckets are `documents` / `structured` / `web`, and no
// vendor brand or internal engine name ever appears — the same guarantee the
// backend service makes.

/** Does this selector name the structured half (bucket or a conn_<id>)? */
function isStructuredSelector(selector: string): boolean {
  return selector === "structured" || selector.startsWith("conn_");
}

/** Does this selector name the corpus half (bucket or an unstructured id)? */
function isDocumentsSelector(selector: string): boolean {
  return (
    selector === "documents" ||
    selector === "drive" ||
    selector === "email" ||
    selector === "chat" ||
    selector === "fs" ||
    selector === "voice"
  );
}

export async function mockRunAgenticAnswer(
  input: ConsoleAgenticRequest,
): Promise<ConsoleAgenticResponse> {
  await delay();
  await delay();
  const query = input.query.trim();
  if (query.length === 0) {
    throw new ConsoleMockError("invalid_request", "La query non può essere vuota.");
  }
  const maxSteps = Math.max(1, Math.min(12, input.max_steps));
  const selectors = input.sources ?? [];
  // Default (omitted) scope is documents + structured, unrestricted.
  const wantDocs = selectors.length === 0 || selectors.some(isDocumentsSelector);
  const wantStructured = selectors.length === 0 || selectors.some(isStructuredSelector);
  const wantWeb = selectors.includes("web");
  const connIds = selectors.filter((s) => s.startsWith("conn_"));
  // One connector in scope → depth ("focused"); several → breadth ("fan_out").
  const focused = connIds.length === 1 && !selectors.includes("structured");
  const focus = wantDocs && wantStructured ? "hybrid" : wantStructured ? "structured" : "documents";

  const steps: ConsoleAgenticStep[] = [];
  let n = 0;
  const push = (step: { type: string; status: string } & Record<string, unknown>): void => {
    n += 1;
    steps.push({ step: n, ...step });
  };

  if (input.conversation_id) {
    push({
      type: "memory",
      status: "ok",
      turns_loaded: 4,
      rewritten: true,
      query: `${query} (riferita al portafoglio del turno precedente)`,
    });
  }
  push({
    type: "plan",
    status: "ok",
    focus,
    sources: selectors.length > 0 ? selectors : ["documents", "structured"],
  });

  const sourcesUsed: string[] = [];

  if (wantDocs) {
    push({
      type: "retrieval",
      status: "ok",
      tier: "T1",
      path: "orchestrator",
      hits: Math.min(input.k, 8),
    });
    sourcesUsed.push("documents");
  }

  if (wantStructured) {
    // One dispatched system by default; a focused run stays on the named one.
    const dispatched = focused ? 1 : 2;
    push({ type: "discovery", status: "ok", seen: 3, dispatched });
    // Bounded by the remaining work budget (retrieval already spent one).
    const sqlSteps = Math.max(1, Math.min(dispatched, maxSteps - (wantDocs ? 1 : 0)));
    for (let i = 0; i < sqlSteps; i += 1) {
      const sqlStep: ConsoleAgenticStep = {
        step: 0,
        type: "sql",
        status: "ok",
        connector_type: "database",
        capability: "database",
        sql: "SELECT cliente, esposizione FROM portafoglio WHERE esposizione > :soglia ORDER BY esposizione DESC LIMIT 50",
        row_count: 12,
      };
      if (input.include_rows) {
        sqlStep.rows = [
          { cliente: "ACME Srl", esposizione: 184200 },
          { cliente: "Beta SpA", esposizione: 97400 },
          { cliente: "Gamma Srl", esposizione: 52100 },
        ];
      }
      n += 1;
      sqlStep.step = n;
      steps.push(sqlStep);
    }
    sourcesUsed.push("structured");
  } else {
    push({
      type: "structured",
      status: "skipped",
      reason: "Metà strutturata non inclusa nello scope della richiesta",
    });
  }

  if (wantWeb) {
    push({ type: "retrieval", status: "ok", tier: "web", path: "web_fusion", hits: 3 });
    sourcesUsed.push("web");
  }

  push({ type: "synthesis", status: "ok" });

  const citations = wantDocs
    ? [
        {
          index: 1,
          document: "Gestionale — Portafoglio crediti",
          document_id: "doc_1043",
          chunk_id: "chunk_doc_1043_0",
        },
        {
          index: 2,
          document: "Scadenzario Q3",
          document_id: "doc_1044",
          chunk_id: "chunk_doc_1044_2",
        },
      ]
    : [];

  const diagnostics: Record<string, unknown> = {
    path: "orchestrator",
    tier: "T1",
    focus,
    structured_strategy: focused ? "focused" : "fan_out",
    took_ms: 4200 + (query.length % 11) * 130,
    queryable_capabilities: ["documents", "database", "spreadsheet"],
    structured_ranking: [
      { capability: "database", score: 0.82 },
      { capability: "spreadsheet", score: 0.41 },
    ],
    scope: {
      requested: selectors.length > 0 ? selectors : null,
      resolved: sourcesUsed,
      policy: input.as_key_id ? { sources: ["drive", "email"], connector_ids: connIds } : null,
    },
  };
  if (input.with_diagnostics) {
    diagnostics.retrieval = { candidates: 48, kept: Math.min(input.k, 8), reranked: true };
    diagnostics.dispatch = { in_scope: focused ? 1 : 2, timed_out: 0 };
  }
  if (input.conversation_id) diagnostics.memory_persisted = true;

  const answer = wantStructured
    ? `In base ai dati interrogati, i clienti a maggiore esposizione sono ACME Srl (184.200), Beta SpA (97.400) e Gamma Srl (52.100)${
        wantDocs ? ", coerentemente con i documenti citati [1][2]" : ""
      }. Risposta generata dal layer mock della console — il backend agentico arriva nel prossimo round.`
    : `In base ai passaggi recuperati, la richiesta «${query}» trova riscontro nei documenti citati [1][2]. Risposta generata dal layer mock della console.`;

  return {
    answer,
    citations,
    sources_used: sourcesUsed,
    steps,
    diagnostics,
    conversation_id: input.conversation_id ?? null,
    degraded: false,
    no_results: false,
  };
}

// ── POST /conversations — open a multi-turn conversation (playground) ──────

export async function mockCreateConversation(title?: string): Promise<ConsoleConversation> {
  await delay();
  const now = new Date().toISOString();
  const trimmed = (title ?? "").trim();
  return {
    conversation_id: `conv_${crypto.randomUUID()}`,
    title: trimmed.length > 0 ? trimmed : "Nuova conversazione",
    message_count: 0,
    created_at: now,
    last_message_at: now,
  };
}

// ── Models + BYOK (per-purpose choice + bring-your-own-key) ────────────────
//
// Mirrors the governed inference profiles. Retrieval purposes are read-only.
const MOCK_BYOK_PROVIDERS = new Set(["openrouter", "openai"]);

/** One-line UI copy per purpose (mirrors the backend ``PURPOSE_LABELS``). */
const MOCK_PURPOSE_LABELS: Record<ConsoleModelPurpose, { label: string; description: string }> = {
  answer: {
    label: "Risposta finale",
    description: "Il modello che scrive la risposta a partire dalle fonti trovate",
  },
  agentic: {
    label: "Ragionamento agentico",
    description:
      "Il modello del loop dell'agente: router, specialisti, interrogazione dei dati strutturati",
  },
  rerank: {
    label: "Riordino dei risultati",
    description: "Il cross-encoder che riordina per rilevanza i passaggi recuperati",
  },
  embedding: {
    label: "Embedding",
    description: "Il modello che trasforma testi e domande in vettori di ricerca",
  },
};

/** Platform default per purpose (mirrors the backend ``HARD_DEFAULTS``). */
const MOCK_PLATFORM_DEFAULTS: Record<ConsoleModelPurpose, { provider: string; model: string }> = {
  answer: { provider: "google", model: "google/gemini-3.5-flash-lite" },
  agentic: { provider: "google", model: "google/gemini-3.5-flash-lite" },
  rerank: { provider: "jina", model: "jinaai/jina-reranker-v3" },
  embedding: { provider: "jina", model: "jinaai/jina-embeddings-v4" },
};

function mockOption(
  purpose: ConsoleModelPurpose,
  option: Omit<ConsoleModelOption, "is_platform_default" | "byok_supported">,
): ConsoleModelOption {
  const def = MOCK_PLATFORM_DEFAULTS[purpose];
  return {
    ...option,
    is_platform_default: option.provider === def.provider && option.model === def.model,
    selectable: purpose === "answer" || purpose === "agentic",
    byok_supported: MOCK_BYOK_PROVIDERS.has(option.provider),
  };
}

// The generative catalog shared by ``answer`` + ``agentic``: the Gemini
// defaults plus the curated current Claude tiers (OpenRouter-routed).
const GENERATIVE_MODELS: ReadonlyArray<
  Omit<ConsoleModelOption, "is_platform_default" | "byok_supported">
> = [
  {
    provider: "google",
    model: "google/gemini-3.5-flash-lite",
    labels: ["vertex-direct"],
    input_usd_per_1m: null,
    output_usd_per_1m: null,
  },
  {
    provider: "openrouter",
    model: "google/gemini-3.5-flash-lite",
    labels: ["openrouter", "byok"],
    input_usd_per_1m: null,
    output_usd_per_1m: null,
  },
  {
    provider: "openrouter",
    model: "anthropic/claude-haiku-4-5",
    labels: ["openrouter", "byok"],
    input_usd_per_1m: null,
    output_usd_per_1m: null,
  },
  {
    provider: "openai",
    model: "openai/gpt-5.6",
    labels: ["openai", "byok"],
    input_usd_per_1m: null,
    output_usd_per_1m: null,
  },
];

function buildMockCatalog(): Record<ConsoleModelPurpose, ConsoleModelOption[]> {
  return {
    answer: GENERATIVE_MODELS.map((o) => mockOption("answer", o)),
    agentic: GENERATIVE_MODELS.map((o) => mockOption("agentic", o)),
    rerank: [
      mockOption("rerank", {
        provider: "jina",
        model: "jinaai/jina-reranker-v3",
        labels: ["jina", "byok"],
        input_usd_per_1m: null,
        output_usd_per_1m: null,
      }),
    ],
    embedding: [
      mockOption("embedding", {
        provider: "jina",
        model: "jinaai/jina-embeddings-v4",
        labels: ["jina", "byok"],
        input_usd_per_1m: null,
        output_usd_per_1m: null,
      }),
    ],
  };
}

/** Build the display-safe config view for a purpose from its override (or default). */
function mockConfigView(purpose: ConsoleModelPurpose): ConsoleModelConfig {
  const meta = MOCK_PURPOSE_LABELS[purpose];
  const override = store.modelConfigs.get(purpose);
  const inference = purpose === "answer" || purpose === "agentic";
  const active = override?.runtimeEnabled ?? false;
  const runtime = {
    configurable: inference,
    runtime_enabled: active,
    provider_payer: active ? ("customer" as const) : ("ando" as const),
    fallback_policy: "strict" as const,
    credential_status: active
      ? ("active" as const)
      : override?.byokSecret
        ? ("saved" as const)
        : ("none" as const),
    delegation_approved: false,
    coverage:
      purpose === "answer"
        ? ["engine_answer_synthesis"]
        : purpose === "agentic"
          ? ["engine_agentic_model", "engine_agentic_inference_helpers"]
          : [],
    tariff: {
      ready: inference,
      markup_pct: inference ? 40 : null,
      reference_pricing_version: inference ? "mock-reference-v1" : null,
      tariff_version: inference ? "mock-tariff-v1" : null,
    },
  };
  if (!override) {
    const def = MOCK_PLATFORM_DEFAULTS[purpose];
    return {
      ...runtime,
      purpose,
      label: meta.label,
      description: meta.description,
      provider: def.provider,
      model: def.model,
      params: {},
      source: "platform_default",
      byok_state: "none",
      byok_masked: null,
      byok_last4: null,
      byok_supported: MOCK_BYOK_PROVIDERS.has(def.provider),
      updated_at: null,
    };
  }
  const secret = override.byokSecret;
  return {
    ...runtime,
    purpose,
    label: meta.label,
    description: meta.description,
    provider: override.provider,
    model: override.model,
    params: { ...override.params },
    source: "tenant",
    byok_state: secret ? "configured" : "none",
    byok_masked: secret ? `********${secret.slice(-4)}` : null,
    byok_last4: secret ? secret.slice(-4) : null,
    byok_supported: MOCK_BYOK_PROVIDERS.has(override.provider),
    updated_at: override.updatedAt,
  };
}

export async function mockListModels(): Promise<ConsoleModelsResponse> {
  await delay();
  return {
    configs: CONSOLE_MODEL_PURPOSES.map((purpose) => mockConfigView(purpose)),
    catalog: buildMockCatalog(),
  };
}

function assertKnownPurpose(purpose: string): asserts purpose is ConsoleModelPurpose {
  if (!(CONSOLE_MODEL_PURPOSES as readonly string[]).includes(purpose)) {
    throw new ConsoleMockError(
      "invalid_request",
      `Purpose sconosciuto '${purpose}'. Validi: ${CONSOLE_MODEL_PURPOSES.join(", ")}.`,
    );
  }
}

export async function mockSetModel(
  purpose: ConsoleModelPurpose,
  body: ConsoleSetModelRequest,
): Promise<ConsoleModelConfigResponse> {
  await delay();
  assertKnownPurpose(purpose);
  if (purpose === "embedding" || purpose === "rerank")
    throw new ConsoleMockError(
      "invalid_request",
      "La pipeline di acquisizione e ricerca è gestita da Ando.",
    );
  const provider = (body.provider ?? "").trim().toLowerCase();
  const model = (body.model ?? "").trim();
  const options = buildMockCatalog()[purpose];
  const match = options.find((o) => o.provider === provider && o.model === model);
  if (!match) {
    // Mirror the backend: a rejection NAMES the supported models.
    const supported = options.map((o) => `${o.provider}/${o.model}`).join(", ");
    throw new ConsoleMockError(
      "invalid_request",
      `Modello '${model}' non disponibile per '${purpose}'. Supportati: ${supported}.`,
    );
  }
  const previous = store.modelConfigs.get(purpose);
  const active = body.runtime_enabled ?? previous?.runtimeEnabled ?? false;
  if (active && (!previous?.byokSecret || previous.provider !== provider || provider === "google"))
    throw new ConsoleMockError(
      "invalid_request",
      "Collega un account approvato prima dell'attivazione.",
    );
  // Changing provider drops the stored BYOK key — it belongs to the previous
  // provider's account (backend upsert semantics).
  const byokSecret = previous && previous.provider === provider ? previous.byokSecret : null;
  store.modelConfigs.set(purpose, {
    provider,
    model,
    params: body.params ? { ...body.params } : {},
    byokSecret,
    runtimeEnabled: active,
    updatedAt: new Date().toISOString(),
  });
  return { config: mockConfigView(purpose) };
}

export async function mockRevertModel(
  purpose: ConsoleModelPurpose,
): Promise<ConsoleModelRevertResponse> {
  await delay();
  assertKnownPurpose(purpose);
  const reverted = store.modelConfigs.delete(purpose);
  return { config: mockConfigView(purpose), reverted };
}

export async function mockSetByok(
  purpose: ConsoleModelPurpose,
  apiKey: string,
): Promise<ConsoleModelConfigResponse> {
  await delay();
  assertKnownPurpose(purpose);
  if (purpose === "embedding" || purpose === "rerank")
    throw new ConsoleMockError(
      "invalid_request",
      "La pipeline di acquisizione e ricerca è gestita da Ando.",
    );
  const secret = (apiKey ?? "").trim();
  if (secret.length < 8) {
    throw new ConsoleMockError("invalid_request", "La chiave deve avere almeno 8 caratteri.");
  }
  if (secret.length > 512) {
    throw new ConsoleMockError("invalid_request", "La chiave supera i 512 caratteri.");
  }
  if (/\s/.test(secret)) {
    throw new ConsoleMockError("invalid_request", "La chiave non può contenere spazi.");
  }
  // Materialise the row from the effective config when still on a default —
  // the dev does not have to "choose a model" before bringing a key.
  const current = mockConfigView(purpose);
  if (!MOCK_BYOK_PROVIDERS.has(current.provider)) {
    throw new ConsoleMockError(
      "invalid_request",
      `Il provider '${current.provider}' non supporta bring-your-own-key. Supportati: ${[
        ...MOCK_BYOK_PROVIDERS,
      ]
        .sort()
        .join(", ")}.`,
    );
  }
  const existing = store.modelConfigs.get(purpose);
  store.modelConfigs.set(purpose, {
    provider: current.provider,
    model: current.model,
    params: existing?.params ?? {},
    byokSecret: secret,
    runtimeEnabled: false,
    updatedAt: new Date().toISOString(),
  });
  return { config: mockConfigView(purpose) };
}

export async function mockDeleteByok(
  purpose: ConsoleModelPurpose,
): Promise<ConsoleByokRemovedResponse> {
  await delay();
  assertKnownPurpose(purpose);
  const existing = store.modelConfigs.get(purpose);
  const removed = Boolean(existing?.byokSecret);
  if (existing?.byokSecret) {
    store.modelConfigs.set(purpose, {
      ...existing,
      byokSecret: null,
      runtimeEnabled: false,
      updatedAt: new Date().toISOString(),
    });
  }
  return { config: mockConfigView(purpose), removed };
}

// ── Flows (console projection of Hive Flows) ───────────────────────────────
//
// Same wire shapes as the product engine (``@ando/core`` hiveFlows types):
// list/detail rows, template gallery, per-flow stats and the manual-run
// result. Stateful: a run bumps the flow's counters so the list reflects it.

function mockFlowGraph(nodeTypes: readonly string[]): ConsoleFlow["graph"] {
  return {
    nodes: nodeTypes.map((node_type, i) => ({
      id: `n${i + 1}`,
      node_type,
      config: {},
      position: { x: 80, y: i * 140 },
    })),
    edges: nodeTypes.slice(1).map((_, i) => ({ source: `n${i + 1}`, target: `n${i + 2}` })),
  };
}

function buildInitialFlows(): ConsoleFlow[] {
  const base = {
    source_config: {},
    timezone: "Europe/Rome",
    is_template: false,
    version: 1,
  } as const;
  return [
    {
      ...base,
      flow_id: "flow-digest",
      name: "Riepilogo giornaliero",
      description: "Ogni mattina l'AI prepara un riepilogo e lo invia via email.",
      graph: mockFlowGraph(["trigger.schedule", "ai.ask", "output.email"]),
      schedule_type: "cron",
      cron_expression: "0 8 * * *",
      requires_approval: false,
      is_active: true,
      status: "active",
      last_run_at: isoDaysAgo(0, 6),
      next_run_at: isoDaysAgo(-1, -18),
      total_runs: 42,
      created_at: isoDaysAgo(30),
      updated_at: isoDaysAgo(2),
    },
    {
      ...base,
      flow_id: "flow-invoice",
      name: "Fattura via email → estrai i dati",
      description: "Quando arriva una fattura, l'AI ne estrae i dati e li notifica.",
      graph: mockFlowGraph([
        "trigger.email_received",
        "ai.extract",
        "logic.approval",
        "output.in_app",
      ]),
      schedule_type: "reactive",
      requires_approval: true,
      is_active: true,
      status: "paused",
      last_run_at: isoDaysAgo(3),
      total_runs: 17,
      created_at: isoDaysAgo(21),
      updated_at: isoDaysAgo(3),
    },
    {
      ...base,
      flow_id: "flow-webhook",
      name: "Webhook → notifica al team",
      description: null,
      graph: mockFlowGraph(["trigger.webhook", "action.message.send"]),
      schedule_type: "webhook",
      requires_approval: false,
      is_active: false,
      status: "draft",
      last_run_at: null,
      total_runs: 0,
      created_at: isoDaysAgo(1),
      updated_at: isoDaysAgo(1),
    },
  ];
}

/** Built-in starter gallery — mirrors the BE ``FLOW_TEMPLATES`` fixture shape. */
const MOCK_FLOW_TEMPLATES: ConsoleFlowTemplatesResponse = {
  templates: [
    {
      id: "daily-digest",
      name: "Riepilogo giornaliero",
      description: "Ogni mattina l'AI prepara un riepilogo e te lo manda via email.",
      category: "Produttività",
      icon: "sunrise",
      graph: mockFlowGraph(["trigger.schedule", "ai.ask", "output.email"]),
    },
    {
      id: "email-triage",
      name: "Avvisami sulle email importanti",
      description:
        "Quando arriva un'email, l'AI valuta se è importante e — se sì — ti manda una notifica.",
      category: "Email",
      icon: "mail",
      graph: mockFlowGraph(["trigger.email_received", "ai.ask", "logic.if", "output.in_app"]),
    },
    {
      id: "webhook-to-slack",
      name: "Webhook → messaggio Slack",
      description: "Un sistema esterno chiama un webhook e tu ricevi un messaggio.",
      category: "Integrazioni",
      icon: "webhook",
      graph: mockFlowGraph(["trigger.webhook", "action.message.send"]),
    },
    {
      id: "invoice-email-extract",
      name: "Fattura via email → estrai i dati",
      description: "Quando arriva una fattura via email, l'AI ne estrae i dati e te li notifica.",
      category: "Amministrazione",
      icon: "receipt",
      graph: mockFlowGraph(["trigger.email_received", "ai.extract", "output.in_app"]),
    },
  ],
};

export async function mockListFlows(
  page = 1,
  pageSize = 20,
  status?: string,
): Promise<ConsoleFlowsResponse> {
  await delay();
  // Mirror the BE default filter: archived flows are hidden unless asked for.
  const all = status
    ? store.flows.filter((f) => f.status === status)
    : store.flows.filter((f) => f.status !== "archived");
  const start = (page - 1) * pageSize;
  return { flows: all.slice(start, start + pageSize), total: all.length };
}

export async function mockFlowTemplates(): Promise<ConsoleFlowTemplatesResponse> {
  await delay();
  return MOCK_FLOW_TEMPLATES;
}

function requireMockFlow(flowId: string): ConsoleFlow {
  const flow = store.flows.find((f) => f.flow_id === flowId);
  if (!flow) throw new ConsoleMockError("not_found", "Flow non trovato.");
  return flow;
}

export async function mockGetFlow(flowId: string): Promise<ConsoleFlow> {
  await delay();
  return requireMockFlow(flowId);
}

export async function mockFlowStats(flowId: string): Promise<ConsoleFlowStats> {
  await delay();
  const flow = requireMockFlow(flowId);
  const total = flow.total_runs;
  const failed = Math.min(total, Math.floor(total / 10));
  const ok = total - failed;
  return {
    total_runs: total,
    success_count: ok,
    failed_count: failed,
    success_rate: total > 0 ? Math.round((ok / total) * 100) / 100 : 0,
    avg_duration_ms: total > 0 ? 5400 : null,
    total_cost_usd: total * 0.004,
    last_run_at: flow.last_run_at ?? null,
  };
}

export async function mockRunFlow(
  flowId: string,
  body?: ConsoleFlowRunRequest,
): Promise<ConsoleFlowRun> {
  await delay();
  const flow = requireMockFlow(flowId);
  const dryRun = body?.dry_run ?? false;
  store.flowRunSeq += 1;
  const now = new Date().toISOString();
  const nodes = flow.graph.nodes;
  // A flow with a HITL approval step parks exactly like the real engine.
  const paused = nodes.some((n) => n.node_type === "logic.approval");
  if (!dryRun && !paused) {
    flow.total_runs += 1;
    flow.last_run_at = now;
  }
  const executed = paused
    ? nodes.slice(
        0,
        Math.max(
          1,
          nodes.findIndex((n) => n.node_type === "logic.approval"),
        ),
      )
    : nodes;
  const run: ConsoleFlowRun = {
    run_id: `run-${store.flowRunSeq}`,
    flow_id: flow.flow_id,
    status: paused ? "pending_approval" : "success",
    trigger_type: "manual",
    is_dry_run: dryRun,
    started_at: now,
    completed_at: paused ? null : now,
    duration_ms: paused ? null : 1280,
    result_summary: paused
      ? "In attesa della tua approvazione."
      : `Completato: ${executed.length} passi eseguiti.`,
    error_message: null,
    total_cost_usd: 0.002,
    node_runs: executed.map((n) => ({
      node_id: n.id,
      node_type: n.node_type,
      status: "success",
      output: {},
      error_message: null,
      duration_ms: 320,
    })),
  };
  recordFlowRunDetail(run, body?.trigger_data ?? {});
  return run;
}

/** Details of every run this mock session started — powers ``mockGetFlowRun``
 * polling exactly like the real ``GET /flows/runs/{run_id}``. */
const flowRunDetailsState = new Map<string, ConsoleFlowRunDetail>();

function recordFlowRunDetail(run: ConsoleFlowRun, triggerData: Record<string, unknown>): void {
  flowRunDetailsState.set(run.run_id, {
    ...run,
    trigger_data: { ...triggerData },
    context: {},
    error_type: null,
    wait_until: null,
    approved_by: null,
    approved_at: null,
    approval_notes: null,
    created_at: run.started_at ?? null,
  });
}

/** ``POST /flows/{id}/run-upload`` — the uploaded PDF becomes the run's
 * ``trigger.content_item_id`` seed; otherwise identical to a manual run. */
export async function mockRunFlowUpload(flowId: string, dryRun: boolean): Promise<ConsoleFlowRun> {
  return mockRunFlow(flowId, {
    dry_run: dryRun,
    trigger_data: { content_item_id: `ci-mock-${Date.now()}`, filename: "documento.pdf" },
  });
}

/** ``GET /flows/runs/{run_id}`` — full run projection; a missing run is a 404. */
export async function mockGetFlowRun(runId: string): Promise<ConsoleFlowRunDetail> {
  await delay();
  const detail = flowRunDetailsState.get(runId);
  if (!detail) throw new ConsoleMockError("not_found", "Esecuzione non trovata.");
  return { ...detail, node_runs: [...detail.node_runs] };
}

/** ``POST /flows`` — a created flow is born as an inactive draft (BE parity). */
export async function mockCreateFlow(payload: ConsoleFlowCreate): Promise<ConsoleFlow> {
  await delay();
  const name = payload.name.trim();
  if (name.length === 0) {
    throw new ConsoleMockError("invalid_request", "Il nome del flow è obbligatorio.");
  }
  store.flowSeq += 1;
  const now = new Date().toISOString();
  const flow: ConsoleFlow = {
    flow_id: `flow-new-${store.flowSeq}`,
    name,
    description: payload.description ?? null,
    graph: payload.graph ?? { nodes: [], edges: [] },
    schedule_type: payload.schedule_type ?? "manual",
    cron_expression: payload.cron_expression ?? null,
    timezone: payload.timezone ?? "Europe/Rome",
    source_config: payload.source_config ?? {},
    requires_approval: payload.requires_approval ?? false,
    is_active: false,
    status: "draft",
    last_run_at: null,
    next_run_at: null,
    total_runs: 0,
    version: 1,
    is_template: false,
    created_at: now,
    updated_at: now,
  };
  store.flows = [flow, ...store.flows];
  return flow;
}

/**
 * ``POST /flows/generate`` — the AI co-pilot. The mock PERSISTS the draft
 * (so the refreshed list shows it) and returns the defensive union shape:
 * both the created ``flow`` and its ``graph`` are present.
 */
export async function mockGenerateFlow(prompt: string): Promise<ConsoleFlowGenerateResult> {
  await delay();
  const trimmed = prompt.trim();
  if (trimmed.length < 3 || trimmed.length > 2000) {
    throw new ConsoleMockError(
      "invalid_request",
      "La descrizione deve essere tra 3 e 2000 caratteri.",
    );
  }
  const draft = await mockCreateFlow({
    name: trimmed.length > 60 ? `${trimmed.slice(0, 57)}…` : trimmed,
    description: trimmed,
    graph: mockFlowGraph(["trigger.schedule", "ai.ask", "output.in_app"]),
    schedule_type: "manual",
  });
  return { ok: true, errors: [], graph: draft.graph, flow: draft };
}

/** ``PATCH /flows/{id}`` — partial update; ``is_active`` drives ``status``. */
export async function mockUpdateFlow(
  flowId: string,
  patch: ConsoleFlowUpdate,
): Promise<ConsoleFlow> {
  await delay();
  const flow = requireMockFlow(flowId);
  const next: ConsoleFlow = {
    ...flow,
    ...patch,
    // Mirror the engine: activation flips the lifecycle status too, unless
    // the patch pins one explicitly.
    status:
      patch.status ??
      (patch.is_active === undefined ? flow.status : patch.is_active ? "active" : "paused"),
    updated_at: new Date().toISOString(),
  };
  store.flows = store.flows.map((f) => (f.flow_id === flowId ? next : f));
  return next;
}

/** ``DELETE /flows/{id}`` — 204; a missing id is a 404. */
export async function mockDeleteFlow(flowId: string): Promise<void> {
  await delay();
  requireMockFlow(flowId);
  store.flows = store.flows.filter((f) => f.flow_id !== flowId);
}

// ── Flows builder surface (console-hosted editor, Aug 2026) ────────────────
//
// Palette + on-demand node schema + the pending-approvals read/approve pair —
// the console projections of the product ``/hive-flows`` builder endpoints
// (same BE registry / ``FlowService`` behind, identical wire shapes). The
// approvals state is self-contained here (module-level, lazily seeded) so the
// long-standing ``store`` literal stays untouched.

function paletteNode(
  partial: Partial<ConsoleNodePaletteItem> &
    Pick<ConsoleNodePaletteItem, "node_type" | "category" | "label" | "icon">,
): ConsoleNodePaletteItem {
  return {
    description: "",
    mode_tier: "essential",
    risk_level: "low",
    connector: null,
    action_type: null,
    outputs: [],
    branches: 0,
    schema_driven: false,
    config_fields: [],
    ...partial,
  };
}

/** Small but real palette — enough for the console editor to be exercisable. */
const MOCK_FLOW_PALETTE: ConsoleFlowPaletteResponse = {
  nodes: [
    paletteNode({
      node_type: "trigger.manual",
      category: "trigger",
      label: "Manuale",
      description: "Avvia il flow a mano.",
      icon: "play",
    }),
    paletteNode({
      node_type: "trigger.schedule",
      category: "trigger",
      label: "Pianificato",
      description: "Avvia il flow a orari prestabiliti.",
      icon: "clock",
      config_fields: [
        {
          key: "cron",
          label: "Pianificazione (cron)",
          type: "text",
          required: true,
          placeholder: "0 8 * * *",
          options: [],
          mappable: false,
          mode_tier: "essential",
        },
      ],
    }),
    paletteNode({
      node_type: "ai.ask",
      category: "ai",
      label: "Chiedi all'AI",
      description: "Una domanda in linguaggio naturale.",
      icon: "sparkles",
      outputs: [{ key: "answer", label: "Risposta", type: "string" }],
      config_fields: [
        {
          key: "prompt",
          label: "Domanda",
          type: "textarea",
          required: true,
          placeholder: "",
          options: [],
          mappable: true,
          mode_tier: "essential",
        },
      ],
    }),
    paletteNode({
      node_type: "ai.sql",
      category: "ai",
      label: "Interroga i tuoi dati",
      description: "Una domanda sui dati collegati, tradotta in SQL.",
      icon: "database",
      outputs: [{ key: "rows", label: "Righe", type: "json" }],
      config_fields: [
        {
          key: "question",
          label: "Domanda",
          type: "textarea",
          required: true,
          placeholder: "",
          options: [],
          mappable: true,
          mode_tier: "essential",
        },
        {
          key: "frozen_sql",
          label: "Query freezata",
          type: "textarea",
          required: false,
          placeholder: "",
          options: [],
          mappable: false,
          mode_tier: "advanced",
        },
      ],
    }),
    paletteNode({
      node_type: "logic.approval",
      category: "logic",
      label: "Richiedi approvazione",
      description: "Mette in pausa il flow finché non approvi.",
      icon: "shield",
    }),
    paletteNode({
      node_type: "output.email",
      category: "output",
      label: "Manda un'email",
      description: "Invia il risultato via email.",
      icon: "mail",
      risk_level: "medium",
      config_fields: [
        {
          key: "to",
          label: "Destinatario",
          type: "text",
          required: true,
          placeholder: "nome@azienda.it",
          options: [],
          mappable: true,
          mode_tier: "essential",
        },
      ],
    }),
    paletteNode({
      node_type: "output.in_app",
      category: "output",
      label: "Notifica in app",
      description: "Una notifica dentro Ando.",
      icon: "bell",
    }),
  ],
};

export async function mockFlowPalette(): Promise<ConsoleFlowPaletteResponse> {
  await delay();
  return MOCK_FLOW_PALETTE;
}

/**
 * ``GET /flows/nodes/{type}/schema`` — the mock has no live connectors, so it
 * mirrors the BE's honest degrade: unresolved, keep the static fields.
 */
export async function mockNodeSchema(
  _nodeType: string,
  _params: { provider?: string; objectType?: string; subAction?: string } = {},
): Promise<ConsoleNodeSchemaResponse> {
  await delay();
  return { config_fields: [], resolved: false };
}

/** Lazily-seeded pending approvals (kept OUT of the legacy store literal). */
let pendingApprovalsState: ConsolePendingApproval[] | null = null;

function pendingApprovals(): ConsolePendingApproval[] {
  if (pendingApprovalsState === null) {
    pendingApprovalsState = [
      {
        run_id: "run-approval-1",
        flow_id: "flow-invoice",
        flow_name: "Fattura via email → estrai i dati",
        result_summary: "In attesa della tua approvazione.",
        created_at: isoDaysAgo(0, 2),
        editable: {
          node_id: "n2",
          key: "body",
          value: "Gentile cliente,\n\nin allegato i dati estratti dalla fattura.",
        },
      },
    ];
  }
  return pendingApprovalsState;
}

export async function mockPendingApprovals(): Promise<ConsolePendingApprovalsResponse> {
  await delay();
  return { approvals: [...pendingApprovals()] };
}

/** ``POST /flows/runs/{run_id}/approve`` — resume; a missing run is a 404. */
export async function mockApproveFlowRun(
  runId: string,
  _editedValue?: string,
): Promise<ConsoleFlowRunApproveResponse> {
  await delay();
  const list = pendingApprovals();
  const found = list.some((a) => a.run_id === runId);
  if (!found) throw new ConsoleMockError("not_found", "Esecuzione non trovata.");
  pendingApprovalsState = list.filter((a) => a.run_id !== runId);
  return { ok: true, status: "running" };
}

/** ``POST /flows/runs/{run_id}/reject`` — terminal "no"; a missing run is a 404. */
export async function mockRejectFlowRun(runId: string): Promise<ConsoleFlowRunApproveResponse> {
  await delay();
  const list = pendingApprovals();
  const found = list.some((a) => a.run_id === runId);
  if (!found) throw new ConsoleMockError("not_found", "Esecuzione non trovata.");
  pendingApprovalsState = list.filter((a) => a.run_id !== runId);
  return { ok: true, status: "rejected" };
}
